import React from "react";
import {
    Bell,
    CopyPlus,
    ExternalLink,
    FileDown,
    Loader2,
    Mail,
    Plus,
    RotateCw,
    Search,
    Smartphone,
    X,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usePolling, getPollingQueryOpts } from "../layout/HoaAdminLayout";
import api from "@/api/client";
import BCVRateHistory from "@/components/rates/BCVRateHistory";
import ResponsiveModal from "@/components/common/ResponsiveModal";
import SegmentedTabs from "@/components/common/SegmentedTabs";
import {
    ConfirmDialog,
    FormField,
    FormFooter,
    FormPanel,
    KpiTile,
    Money,
    OverdueChip,
    PaymentCardShell,
    PaymentEmptyState,
    PaymentListSkeleton,
    QuietChip,
    RowActionMenu,
    StatusChip as PayStatusChip,
    StatusWell,
    WaitingLabel,
    areaClass,
    fieldClass,
} from "@/components/payments/PaymentBits";
import { usePaymentLabels } from "@/lib/paymentLabels";
import {
    BRAND,
    OVERDUE_TONE,
    STATUS_TONE,
    WAITING_TONE,
    isAwaitingHoaConfirmation,
    isOverdue,
    isSettled,
    tileStyle,
    waitingOn,
} from "@/lib/paymentStyles";
import {
    type Invoice as ApiInvoice,
    type PaymentMethod,
    type PaymentFlow,
    type BulkInvoiceData,
    type CreateInvoiceData,
    type BCVRate,
    bulkCreateInvoices,
    createInvoice,
    deleteInvoice,
    getInvoices,
    finalizePayment,
    recordManualPayment,
    type ManualPaymentMethod,
    getBuildingOptions,
    getResidentsByBuilding,
    getUnitsByBuilding,
    searchResidents,
    getBCVRate,
    todayInCaracas,
    getInvoicePaymentDetails,
    verifyPayment,
    sendOverdueReminder,
    bulkSendReminders,
    getOverdueReminderCandidates,
    getNotificationLog,
    resendNotification,
    type BulkReminderResult,
    type InvoicePaymentDetails,
    type InvoiceNotification,
    type NotificationStatus,
    type NotificationType,
} from "@/api/payments";
import { listResidents, type ResidentList, type ResidentRole } from "@/api";
import { listUnits } from "@/api/units";
import { listBuildings, type BuildingList as Condo } from "@/api/buildings";
import { toast } from "sonner";
import { mediaUrl, openMedia } from "@/lib/mediaUrl";
import { CommissionBreakdown } from "@/components/CommissionBreakdown";
import { PaymentDetailsCard } from "@/components/PaymentDetailsCard";
import { useLanguage } from "@/contexts/LanguageContext";

/** =========================
 * Types
 * ========================= */
type InvoiceStatus =
    | "DRAFT"
    | "SENT"
    | "UNPAID"
    | "PARTIALLY_PAID"
    | "PENDING_VERIFICATION"
    | "PENDING_HOA_TRANSFER"
    | "PAID"
    | "CANCELLED";
type AmountSource = "FIXED" | "UNIT_RENT";

type Payment = {
    id: string;
    unitNumber: string;
    residentName: string;
    residentEmail?: string;
    period: string; // "YYYY-MM"
    total_amount: number;
    status: InvoiceStatus;
    payment_flow: PaymentFlow;
    payment_method?: PaymentMethod;
    due_date: string; // ISO
    marked_paid_at?: string; // ISO
    verified_at?: string;
    /** Super admin sent the money on to the HOA. Together with status=PAID,
     *  PLATFORM_RELAY and a null finalized_at this is "awaiting your
     *  confirmation" -- see isAwaitingHoaConfirmation. */
    forwarded_at?: string;
    finalized_at?: string;
    notes?: string;
    created_at: string;
    updated_at: string;
    /** VES/USD rate locked at invoice creation (Venezuela compliance) */
    bcv_rate_snapshot?: number;
    /** When the BCV rate was captured */
    bcv_rate_date?: string;
    /** Server's own overdue verdict. Optional: older rows may not carry it,
     *  which is why isOverdue() falls back to comparing the due date. */
    is_overdue?: boolean;
    /** The untransformed invoice. Carried so the awaiting-confirmation view
     *  can show the forwarded amount and commission, which this projection
     *  deliberately drops for the list. */
    raw: ApiInvoice;
};

type Unit = {
    id: string;
    number: string | null;
    unit_code: string | null;
    monthly_fee: string; // decimal as string from backend
    building_id: string | null; // FK to Condo/Building
};

type HoaPaymentStateKey =
    | "WAITING_RESIDENT"
    | "OVERDUE_FROM_RESIDENT"
    | "PARTIALLY_PAID"
    | "PENDING_VERIFICATION"
    | "PENDING_HOA_TRANSFER"
    | "PAID_WAITING_CONFIRM"
    | "COMPLETED"
    | "DRAFT"
    | "CANCELLED";

type HoaPaymentState = {
    key: HoaPaymentStateKey;
    label: string;
    bg: string;
    fg: string;
    subtitle?: string;
};

function getDirectHoaStateCopy(language: string) {
    if (language === "es") {
        return {
            pendingLabel: "Residente pagó (revisión HOA)",
            pendingSubtitle: "El residente subió su comprobante. Falta revisión de la administración HOA.",
            confirmedLabel: "Pago confirmado",
            confirmedSubtitle: "La administración HOA revisó y confirmó este pago.",
        };
    }
    return {
        pendingLabel: "Resident paid (HOA review)",
        pendingSubtitle: "Resident submitted payment proof. Pending HOA admin review.",
        confirmedLabel: "Payment confirmed",
        confirmedSubtitle: "HOA admin reviewed and confirmed this payment.",
    };
}

function getDirectHoaReviewCopy(language: string) {
    if (language === "es") {
        return {
            reviewTitle: "Revisión de pago directo",
            reviewSubtitle: "Revisa el comprobante del residente y decide si aprobar o rechazar.",
            notesLabel: "Notas de revisión (opcional)",
            notesPlaceholder: "Agrega una observación para el residente o para auditoría.",
            approveLabel: "Aprobar pago",
            rejectLabel: "Rechazar pago",
            approvedToast: "Pago aprobado y confirmado por HOA.",
            rejectedToast: "Pago rechazado. La factura volvió a pendiente.",
            proofTitle: "Comprobante del residente",
            idTitle: "Documento de identificación",
            residentNotesTitle: "Notas del residente",
            noProofWarning: "No se encontró comprobante del residente para revisar.",
        };
    }
    return {
        reviewTitle: "Direct payment review",
        reviewSubtitle: "Review the resident proof and decide whether to approve or reject.",
        notesLabel: "Review notes (optional)",
        notesPlaceholder: "Add a note for the resident or for audit history.",
        approveLabel: "Approve payment",
        rejectLabel: "Reject payment",
        approvedToast: "Payment approved and confirmed by HOA.",
        rejectedToast: "Payment rejected. Invoice moved back to unpaid.",
        proofTitle: "Resident payment proof",
        idTitle: "ID verification",
        residentNotesTitle: "Resident notes",
        noProofWarning: "No resident proof was found for review.",
    };
}

function getHoaPaymentState(p: Payment, t: (key: string) => string, language: string): HoaPaymentState {
    const now = new Date();
    const directCopy = getDirectHoaStateCopy(language);
    const overdue =
        (p.status === "UNPAID" || p.status === "SENT") &&
        new Date(p.due_date) < now;

    // Draft invoice not yet sent
    if (p.status === "DRAFT") {
        return {
            key: "DRAFT",
            label: t("hoa.payments.statusLabel.draft"),
            bg: "rgba(199,210,254,0.45)",
            fg: "#1E3A8A",
            subtitle: t("hoa.payments.statusSubtitle.draft"),
        };
    }

    if (p.status === "CANCELLED") {
        return {
            key: "CANCELLED",
            label: t("hoa.payments.statusLabel.cancelled"),
            bg: "rgba(239,68,68,0.12)",
            fg: "#991B1B",
            subtitle: t("hoa.payments.statusSubtitle.cancelled"),
        };
    }

    // Resident reported a payment and uploaded proof to Urbe360
    if (p.status === "PENDING_VERIFICATION") {
        if (p.payment_flow === "DIRECT_HOA") {
            return {
                key: "PENDING_VERIFICATION",
                label: directCopy.pendingLabel,
                bg: "rgba(245,158,11,0.12)",
                fg: "#78350F",
                subtitle: directCopy.pendingSubtitle,
            };
        }
        return {
            key: "PENDING_VERIFICATION",
            label: t("hoa.payments.statusLabel.residentPaid"),
            bg: "rgba(245,158,11,0.12)",
            fg: "#78350F",
            subtitle: t("hoa.payments.statusSubtitle.residentPaid"),
        };
    }

    // Urbe360 verified and is transferring to HOA
    if (p.status === "PENDING_HOA_TRANSFER") {
        return {
            key: "PENDING_HOA_TRANSFER",
            label: t("hoa.payments.statusLabel.urbe360Verified"),
            bg: "rgba(59,130,246,0.12)",
            fg: "#1E40AF",
            subtitle: t("hoa.payments.statusSubtitle.urbe360Verified"),
        };
    }

    // Urbe360 verified and paid HOA, but HOA hasn't confirmed yet
    if (p.status === "PAID" && !p.finalized_at) {
        if (p.payment_flow === "DIRECT_HOA") {
            return {
                key: "COMPLETED",
                label: directCopy.confirmedLabel,
                bg: "rgba(39,174,96,0.12)",
                fg: "#166534",
                subtitle: directCopy.confirmedSubtitle,
            };
        }
        return {
            key: "PAID_WAITING_CONFIRM",
            label: t("hoa.payments.statusLabel.urbe360PaidHoa"),
            bg: "rgba(39,174,96,0.12)",
            fg: "#166534",
            subtitle: t("hoa.payments.statusSubtitle.urbe360PaidHoa"),
        };
    }

    // HOA confirmed receipt
    if (p.status === "PAID" && p.finalized_at) {
        if (p.payment_flow === "DIRECT_HOA") {
            return {
                key: "COMPLETED",
                label: directCopy.confirmedLabel,
                bg: "rgba(39,174,96,0.12)",
                fg: "#166534",
                subtitle: directCopy.confirmedSubtitle,
            };
        }
        return {
            key: "COMPLETED",
            label: t("hoa.payments.statusLabel.completed"),
            bg: "rgba(39,174,96,0.12)",
            fg: "#166534",
            subtitle: t("hoa.payments.statusSubtitle.completed"),
        };
    }

    // Partially paid — some money received, balance still outstanding
    if (p.status === "PARTIALLY_PAID") {
        return {
            key: "PARTIALLY_PAID",
            label: t("hoa.payments.status.partiallyPaid"),
            bg: "rgba(245,158,11,0.18)",
            fg: "#92400E",
            subtitle: t("hoa.payments.statusSubtitle.partiallyPaid"),
        };
    }

    // Resident still hasn't paid, and invoice is overdue
    if (overdue) {
        return {
            key: "OVERDUE_FROM_RESIDENT",
            label: t("hoa.payments.statusLabel.overdue"),
            bg: "rgba(248,113,113,0.12)",
            fg: "#991B1B",
            subtitle: t("hoa.payments.statusSubtitle.overdue"),
        };
    }

    // Default: waiting for resident payment (UNPAID / SENT, not overdue)
    return {
        key: "WAITING_RESIDENT",
        label: t("hoa.payments.statusLabel.waiting"),
        bg: "rgba(59,130,246,0.12)",
        fg: "#1E3A8A",
        subtitle: t("hoa.payments.statusSubtitle.waiting"),
    };
}

/** =========================
 * Helpers
 * ========================= */
const NOW = new Date();
const THIS_YYYYMM = `${NOW.getFullYear()}-${String(NOW.getMonth() + 1).padStart(
    2,
    "0"
)}`;
const LAST_DT = new Date(NOW.getFullYear(), NOW.getMonth() - 1, 1);
const LAST_YYYYMM = `${LAST_DT.getFullYear()}-${String(
    LAST_DT.getMonth() + 1
).padStart(2, "0")}`;

// Transform API invoice to display format
function transformInvoice(inv: ApiInvoice): Payment {
    return {
        id: inv.id,
        // Backend sends `unit_code` (e.g. "A-302"), not a nested unit object
        unitNumber: inv.unit_code || "—",
        // Backend sends `resident_name` / `resident_email`
        residentName: inv.resident_name || inv.resident_email || "—",
        residentEmail: inv.resident_email,
        period: inv.period,
        total_amount: parseFloat(inv.total_amount),
        status: inv.status as InvoiceStatus,
        payment_flow: inv.payment_flow,
        payment_method: inv.payment_method,
        due_date: inv.due_date,
        marked_paid_at: inv.marked_paid_at,
        verified_at: inv.verified_at,
        forwarded_at: inv.forwarded_at,
        finalized_at: inv.finalized_at,
        // Use actual invoice notes, not resident name
        notes: inv.notes ?? "",
        created_at: inv.created_at,
        updated_at: inv.updated_at,
        bcv_rate_snapshot: inv.bcv_rate_snapshot ? parseFloat(inv.bcv_rate_snapshot) : undefined,
        bcv_rate_date: inv.bcv_rate_date,
        is_overdue: inv.is_overdue,
        raw: inv,
    };
}

/** =========================
 * Page
 * ========================= */
export default function PaymentsPage() {
    const { t, language } = useLanguage();
    const qc = useQueryClient();
    const { enabled, intervalMs } = usePolling();
    const locale = language === "es" ? "es-VE" : "en-US";

    // Incoming payments used to be a fourth tab. It is a filter now: those
    // invoices are already IN this list (status PAID, forwarded, not yet
    // finalized) and were rendering green as settled while actually waiting
    // on the admin. A tab duplicated rows that only needed relabelling.
    const [activeTab, setActiveTab] = React.useState<"invoices" | "notifications" | "rates">("invoices");
    // Set when someone clicks the rate on a payment proof: switch to the
    // rates tab and scroll that day into view, so "why is this figure what
    // it is" is one click rather than a mental date lookup.
    const [rateDayFocus, setRateDayFocus] = React.useState<string | null>(null);
    // window.confirm is an unstyled OS dialog that ignores the app's language
    // on some platforms and cannot show what is about to be lost.
    const [confirmDelete, setConfirmDelete] = React.useState<Payment | null>(null);

    // Bulk reminder result modal
    const [bulkReminderResult, setBulkReminderResult] = React.useState<BulkReminderResult | null>(null);

    const [reminderOpen, setReminderOpen] = React.useState(false);

    // views / filters
    const [q, setQ] = React.useState("");
    const [period, setPeriod] = React.useState<"THIS" | "LAST" | "ALL">("THIS");
    const [statusFilter, setStatusFilter] = React.useState<
        "ALL" | InvoiceStatus
    >("ALL");
    // Overdue is its own filter dimension because it is NOT a status: it is
    // derived from the due date against today, and an invoice can be overdue
    // while SENT, UNPAID or PARTIALLY_PAID. Folding it into statusFilter would
    // mean picking one of those three and hiding the other two.
    const [overdueOnly, setOverdueOnly] = React.useState(false);
    // Money the platform says it sent; the HOA has not confirmed arrival.
    // Four conditions, not a status -- see isAwaitingHoaConfirmation.
    const [incomingOnly, setIncomingOnly] = React.useState(false);
    const [sortBy, setSortBy] = React.useState<
        "dueDate" | "amount" | "updatedAt"
    >("dueDate");
    const [sortDir, setSortDir] = React.useState<"asc" | "desc">("asc");

    // modals
    const [createOpen, setCreateOpen] = React.useState(false);
    const [bulkOpen, setBulkOpen] = React.useState(false);
    const [details, setDetails] = React.useState<Payment | null>(null);
    const [incomingDetails, setIncomingDetails] = React.useState<ApiInvoice | null>(null);
    const [proofsPayment, setProofsPayment] = React.useState<Payment | null>(null);

    const listQuery = useQuery({
        queryKey: ["hoa-admin", "payments"],
        queryFn: async () => {
            const invoices = await getInvoices();
            return invoices.map(transformInvoice);
        },
        ...getPollingQueryOpts(enabled, intervalMs),
    });

    // Query for notification delivery log
    const notifLogQuery = useQuery({
        queryKey: ["hoa-admin", "notification-log"],
        queryFn: () => getNotificationLog(),
        enabled: activeTab === "notifications",
    });
    const [notifTypeFilter, setNotifTypeFilter] = React.useState<"ALL" | "INVOICE_SENT" | "OVERDUE_REMINDER">("ALL");
    const [notifStatusFilter, setNotifStatusFilter] = React.useState<"ALL" | NotificationStatus>("ALL");

    const residentsQuery = useQuery({
        queryKey: ["hoa-admin", "residents", "for-bulk"],
        queryFn: listResidents,
        ...getPollingQueryOpts(false, 0),
    });
    const unitsQuery = useQuery({
        queryKey: ["hoa-admin", "units", "for-bulk"],
        queryFn: () => listUnits(),
        ...getPollingQueryOpts(false, 0),
    });
    const condosQuery = useQuery({
        queryKey: ["hoa-admin", "buildings"],
        queryFn: listBuildings,
        ...getPollingQueryOpts(false, 0),
    });

    // BCV Exchange Rate query
    const bcvRateQuery = useQuery({
        queryKey: ["bcv-rate"],
        queryFn: () => getBCVRate(),
        staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
        refetchInterval: 5 * 60 * 1000, // Auto-refresh the frontend UI every 5 minutes
        refetchOnWindowFocus: true,     // Let it refresh immediately if user comes back to the tab
    });

    const bulkMutation = useMutation({
        mutationFn: async (payload: {
            resident_ids: string[];
            period: string;
            due_date: string;
            payment_method?: PaymentMethod;
            notes?: string;
            tenant_only: boolean;
            use_unit_fee: boolean;
            fixed_amount?: number;
        }) => {
            const data: BulkInvoiceData = {
                resident_ids: payload.resident_ids,
                period: payload.period,
                due_date: payload.due_date,
                payment_method: payload.payment_method,
                notes: payload.notes,
                tenant_only: payload.tenant_only,
                use_unit_fee: payload.use_unit_fee,
                fixed_amount: payload.fixed_amount,
            };
            return await bulkCreateInvoices(data);
        },
        onSuccess: (res) => {
            qc.invalidateQueries({ queryKey: ["hoa-admin", "payments"] });
            setBulkOpen(false);
            toast.success(t("hoa.payments.bulk.success", { count: res.created }));
            if (res.skipped_no_fee > 0) {
                toast.warning(
                    t("hoa.payments.bulk.skippedNoFee", {
                        count: res.skipped_no_fee,
                    })
                );
            }
            if (res.skipped_existing > 0) {
                toast.info(
                    t("hoa.payments.bulk.skippedExisting", {
                        count: res.skipped_existing,
                    })
                );
            }
        },
        onError: (error: any) => {
            // Handle unique constraint violation
            if (error?.response?.data?.non_field_errors) {
                const errors = error.response.data.non_field_errors;
                if (errors.some((e: string) => e.includes("unique set"))) {
                    toast.error(t("hoa.payments.bulk.errors.alreadyExists"));
                    return;
                }
            }
            toast.error(
                error?.response?.data?.error || t("hoa.payments.bulk.failed")
            );
        },
    });

    const createMutation = useMutation({
        mutationFn: async (data: CreateInvoiceData) =>
            await createInvoice(data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["hoa-admin", "payments"] });
            setCreateOpen(false);
            toast.success(t("hoa.payments.toast.created"));
        },
        onError: (error: any) => {
            // Handle specific error from backend
            if (error?.response?.data?.error) {
                const errorData = error.response.data;
                toast.error(errorData.error, {
                    description:
                        errorData.suggestion ||
                        t("hoa.payments.errors.retryDifferentValues"),
                    duration: 6000,
                });
                return;
            }

            // Handle unique constraint violation (fallback)
            if (error?.response?.data?.non_field_errors) {
                const errors = error.response.data.non_field_errors;
                if (errors.some((e: string) => e.includes("unique set"))) {
                    toast.error(t("hoa.payments.errors.invoiceExistsForUnitPeriod"));
                    return;
                }
            }

            toast.error(
                error?.response?.data?.detail || t("hoa.payments.toast.createFailed")
            );
        },
    });

    const finalizeMutation = useMutation({
        mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
            finalizePayment(id, { notes }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["hoa-admin", "payments"] });
            qc.invalidateQueries({ queryKey: ["hoa-admin", "incoming-payments"] });
            setIncomingDetails(null);
            toast.success(t("hoa.payments.toast.confirmed"));
        },
        onError: (error: any) => {
            toast.error(
                error?.response?.data?.error || t("hoa.payments.toast.confirmFailed")
            );
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => deleteInvoice(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["hoa-admin", "payments"] });
            toast.success(t("hoa.payments.toast.deleted"));
        },
        onError: (error: any) => {
            toast.error(
                error?.response?.data?.error || 
                error?.response?.data?.detail || 
                t("hoa.payments.toast.deleteFailed")
            );
        },
    });

    const rows = React.useMemo(() => {
        let data = (listQuery.data ?? []).slice();
        // Remove archived filter since backend doesn't have this field
        if (period === "THIS")
            data = data.filter((p) => p.period === THIS_YYYYMM);
        else if (period === "LAST")
            data = data.filter((p) => p.period === LAST_YYYYMM);
        if (statusFilter !== "ALL")
            data = data.filter((p) => p.status === statusFilter);
        if (overdueOnly)
            data = data.filter((p) => isOverdue(p.status, p.due_date, p.is_overdue));
        if (incomingOnly) data = data.filter(isAwaitingHoaConfirmation);
        if (q.trim()) {
            const s = q.trim().toLowerCase();
            data = data.filter(
                (p) =>
                    p.residentName.toLowerCase().includes(s) ||
                    p.unitNumber.toLowerCase().includes(s) ||
                    (p.notes ?? "").toLowerCase().includes(s)
            );
        }
        data.sort((a, b) => {
            const dir = sortDir === "asc" ? 1 : -1;
            if (sortBy === "amount")
                return (a.total_amount - b.total_amount) * dir;
            if (sortBy === "updatedAt")
                return (
                    (+new Date(a.updated_at) - +new Date(b.updated_at)) * dir
                );
            return (+new Date(a.due_date) - +new Date(b.due_date)) * dir;
        });
        return data;
    }, [listQuery.data, period, statusFilter, overdueOnly, incomingOnly, q, sortBy, sortDir]);

    // KPIs (this month)
    const kpis = React.useMemo(() => {
        const monthRows = (listQuery.data ?? []).filter(
            (p) => p.period === THIS_YYYYMM
        );
        const expected = monthRows.reduce((a, b) => a + b.total_amount, 0);
        const paid = monthRows
            .filter((r) => r.status === "PAID")
            .reduce((a, b) => a + b.total_amount, 0);
        const sent = monthRows.filter(
            (r) => r.status === "SENT" || r.status === "UNPAID"
        ).length;
        const rate = expected ? Math.round((paid / expected) * 100) : 0;

        // Counted across EVERY period, not just this month: a proof waiting
        // on you from two months ago is still waiting on you, and a tile
        // that hid it would be worse than no tile.
        const all = listQuery.data ?? [];
        const awaitingReview = all.filter((r) => r.status === "PENDING_VERIFICATION").length;
        const overdue = all.filter((r) => isOverdue(r.status, r.due_date, r.is_overdue)).length;
        const incoming = all.filter(isAwaitingHoaConfirmation).length;

        return { expected, paid, sent, rate, awaitingReview, overdue, incoming };
    }, [listQuery.data]);

    function toggleSort(col: "dueDate" | "amount" | "updatedAt") {
        if (sortBy === col) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortBy(col);
            setSortDir("asc");
        }
    }

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-[18px] font-semibold text-[color:var(--color-fg)]">
                        {t("hoa.payments.title")}
                    </h1>
                    <p className="text-sm text-[--color-muted]">
                        {t("hoa.payments.subtitle")}
                    </p>
                </div>
                {/* One toolbar, one hierarchy.
                    This was four buttons at four different visual weights --
                    a bordered ghost, a full brand gradient, another ghost and
                    an amber one -- sitting in a row that wrapped differently
                    at every width. Nothing about the arrangement said which
                    was the main action, and the amber belonged to no palette
                    on the page.

                    Now: one primary, two matched secondaries, one icon-only
                    refresh, all 40px. Same shape as the issues page header. */}
                {activeTab === "invoices" && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => listQuery.refetch()}
                            disabled={listQuery.isFetching}
                            aria-label={t("common.refresh")}
                            title={t("common.refresh")}
                            className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-gray-600 ring-1 ring-gray-200 transition hover:bg-gray-50 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93]"
                        >
                            <RotateCw
                                className={`h-4 w-4 ${listQuery.isFetching ? "animate-spin" : ""}`}
                            />
                        </button>

                        <ToolbarButton
                            icon={Bell}
                            label={t("hoa.payments.bulkReminder")}
                            onClick={() => setReminderOpen(true)}
                            title={t("hoa.payments.tooltip.bulkReminder")}
                            // The count earns the emphasis; an empty queue is
                            // just another neutral button.
                            badge={kpis.overdue > 0 ? kpis.overdue : undefined}
                        />
                        <ToolbarButton
                            icon={CopyPlus}
                            label={t("hoa.payments.bulkCreate")}
                            onClick={() => setBulkOpen(true)}
                            title={t("hoa.payments.tooltip.bulkGenerate")}
                        />

                        <button
                            onClick={() => setCreateOpen(true)}
                            title={t("hoa.payments.tooltip.createSingle")}
                            className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-xl px-3.5 text-sm font-semibold text-white shadow-sm transition active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93] focus-visible:ring-offset-2 sm:px-4"
                            style={{
                                background: `linear-gradient(100deg, ${BRAND} 0%, ${BRAND} 55%, #27AE60 160%)`,
                            }}
                        >
                            <Plus className="h-4 w-4" />
                            <span className="hidden sm:inline">
                                {t("hoa.payments.createInvoice")}
                            </span>
                        </button>
                    </div>
                )}
            </div>

            <SegmentedTabs
                ariaLabel={t("hoa.payments.title")}
                value={activeTab}
                onChange={setActiveTab}
                items={[
                    {
                        key: "invoices" as const,
                        label: t("hoa.payments.tabs.invoices"),
                        icon: <InvoiceIcon className="h-4 w-4" />,
                    },
                    {
                        key: "notifications" as const,
                        label: t("hoa.payments.tabs.notifications"),
                        icon: <Bell className="h-4 w-4" />,
                    },
                    {
                        key: "rates" as const,
                        label: t("hoa.payments.tabs.rates"),
                        icon: <TrendingUpIcon className="h-4 w-4" />,
                    },
                ]}
            />

            {bulkReminderResult && (
                <div className="rounded-3xl bg-amber-50 p-4 ring-1 ring-amber-200">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-amber-900">{t("hoa.payments.bulkReminderTitle")}</h3>
                        <button onClick={() => setBulkReminderResult(null)} className="text-amber-500 hover:text-amber-700 text-lg font-bold">&times;</button>
                    </div>
                    <div className="flex gap-6 mb-3 text-sm">
                        <span className="text-gray-600">{t("hoa.payments.bulkReminderTotal")} <strong>{bulkReminderResult.total}</strong></span>
                        <span className="text-emerald-700">{t("hoa.payments.bulkReminderSent")} <strong>{bulkReminderResult.sent}</strong></span>
                        <span className="text-gray-500">{t("hoa.payments.bulkReminderMocked")} <strong>{bulkReminderResult.mocked}</strong></span>
                        <span className="text-red-600">{t("hoa.payments.bulkReminderFailed")} <strong>{bulkReminderResult.failed}</strong></span>
                    </div>
                    {bulkReminderResult.details.length > 0 && (
                        <div className="max-h-48 overflow-y-auto space-y-1">
                            {bulkReminderResult.details.map((d) => (
                                <div key={d.invoice_id} className="flex items-center gap-3 text-xs bg-white rounded-lg px-3 py-1.5">
                                    <span className={`font-semibold w-14 shrink-0 ${
                                        d.status === "SENT" ? "text-emerald-600" :
                                        d.status === "FAILED" || d.status === "ERROR" ? "text-red-500" : "text-gray-400"
                                    }`}>{d.status === "SKIPPED" ? t("hoa.notifLog.status.skipped") : d.status}</span>
                                    <span className="font-medium text-gray-800 truncate">{d.resident}</span>
                                    <span className="text-gray-400 shrink-0">{d.unit} &bull; {d.period}</span>
                                    <span className="text-gray-300 ml-auto shrink-0">{t("hoa.payments.bulkReminderDaysOverdue", {count: String(d.days_overdue)})}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <ConfirmDialog
                open={!!confirmDelete}
                title={t("hoa.payments.modal.deleteTitle")}
                message={
                    confirmDelete
                        ? t("hoa.payments.modal.deleteMessageWithPeriod", {
                              resident: confirmDelete.residentName,
                              period: confirmDelete.period,
                          })
                        : ""
                }
                confirmLabel={t("common.delete")}
                busy={deleteMutation.isPending}
                onCancel={() => setConfirmDelete(null)}
                onConfirm={() => {
                    if (confirmDelete) deleteMutation.mutate(confirmDelete.id);
                    setConfirmDelete(null);
                }}
            />

            {/* Tab Content: BCV rates */}
            {activeTab === "rates" && <BCVRateHistory highlightDate={rateDayFocus} />}

            {/* Tab Content: Invoices */}
            {activeTab === "invoices" && (
                <>
            {/* KPIs. Scrollable on a phone rather than a 2x2 block that
                pushes the list below the fold.

                ALL FOUR filter. Two of them used to be inert -- no onClick, so
                KpiTile rendered a <div> -- while looking identical to the two
                that worked, which made them read as broken buttons rather than
                as statistics. Either a tile acts or it must not look like it
                could, and every one of these four has an obvious action. */}
            <div
                className={`-mx-4 flex snap-x gap-2.5 overflow-x-auto px-4 pb-1 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 ${
                    kpis.incoming > 0 ? "lg:grid-cols-5" : "lg:grid-cols-4"
                }`}
            >
                {/* Each tile SETS THE PERIOD ITS NUMBER WAS COUNTED OVER.
                    Expected and Paid are this month; Awaiting review and
                    Overdue span every period, because a proof waiting on you
                    from two months ago is still waiting on you. The list
                    defaults to this month, so without setting the period a
                    tile reading 15 filtered down to an empty August and the
                    control looked broken. */}
                <KpiTile
                    label={t("hoa.payments.kpi.expected")}
                    value={fmtMoney(kpis.expected)}
                    sub={t("hoa.payments.kpi.showAll")}
                    tone={tileStyle(WAITING_TONE.NOBODY)}
                    active={period === "THIS" && statusFilter === "ALL" && !overdueOnly}
                    onClick={() => {
                        setPeriod("THIS");
                        setStatusFilter("ALL");
                        setOverdueOnly(false);
                    }}
                />
                <KpiTile
                    label={t("hoa.payments.kpi.paid")}
                    value={fmtMoney(kpis.paid)}
                    sub={t("hoa.payments.kpi.rateShort", { rate: String(kpis.rate) })}
                    tone={tileStyle(WAITING_TONE.SETTLED)}
                    active={period === "THIS" && statusFilter === "PAID"}
                    onClick={() => {
                        const on = period === "THIS" && statusFilter === "PAID";
                        setPeriod("THIS");
                        // A paid invoice is never overdue, so leaving the
                        // overdue filter on would guarantee an empty list.
                        setOverdueOnly(false);
                        setStatusFilter(on ? "ALL" : "PAID");
                    }}
                />
                <KpiTile
                    label={t("hoa.payments.waiting.you")}
                    value={String(kpis.awaitingReview)}
                    sub={t("hoa.payments.kpi.needsReview")}
                    tone={tileStyle(WAITING_TONE.YOU)}
                    active={period === "ALL" && statusFilter === "PENDING_VERIFICATION"}
                    onClick={() => {
                        const on = period === "ALL" && statusFilter === "PENDING_VERIFICATION";
                        setOverdueOnly(false);
                        setPeriod(on ? "THIS" : "ALL");
                        setStatusFilter(on ? "ALL" : "PENDING_VERIFICATION");
                    }}
                />
                {/* Overdue spans SENT, UNPAID and PARTIALLY_PAID, so it clears
                    statusFilter rather than setting one of the three. */}
                <KpiTile
                    label={t("hoa.payments.kpi.overdue")}
                    value={String(kpis.overdue)}
                    sub={kpis.overdue > 0 ? t("hoa.payments.kpi.overdueSub") : undefined}
                    tone={tileStyle(kpis.overdue > 0 ? OVERDUE_TONE : WAITING_TONE.NOBODY)}
                    active={overdueOnly}
                    onClick={() => {
                        const on = overdueOnly;
                        setStatusFilter("ALL");
                        setIncomingOnly(false);
                        setPeriod(on ? "THIS" : "ALL");
                        setOverdueOnly(!on);
                    }}
                />
                {/* Only when there IS money to confirm. Across production this
                    is 3 invoices in 746, so a permanent tile reading 0 would
                    cost every HOA a column to say "nothing happened". */}
                {kpis.incoming > 0 && (
                    <KpiTile
                        label={t("hoa.payments.kpi.incoming")}
                        value={String(kpis.incoming)}
                        sub={t("hoa.payments.kpi.incomingSub")}
                        tone={tileStyle(WAITING_TONE.YOU)}
                        active={incomingOnly}
                        onClick={() => {
                            const on = incomingOnly;
                            setStatusFilter("ALL");
                            setOverdueOnly(false);
                            setPeriod(on ? "THIS" : "ALL");
                            setIncomingOnly(!on);
                        }}
                    />
                )}
            </div>

            {/* BCV Exchange Rate Card */}
            <div className="rounded-3xl bg-white/80 px-4 py-3 ring-1 ring-black/5 backdrop-blur-xl">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span
                            className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl"
                            style={{ background: "#E7EBF8", color: "#1B3480" }}
                        >
                            <ExchangeIcon className="h-5 w-5" />
                        </span>
                        <div>
                            <p className="text-sm font-medium text-[--color-muted]">
                                {t("hoa.payments.bcv.title")}
                            </p>
                            {bcvRateQuery.isLoading ? (
                                <p className="text-lg font-semibold text-gray-400">
                                    {t("hoa.payments.bcv.loading")}
                                </p>
                            ) : bcvRateQuery.isError ? (
                                <p className="text-sm text-rose-600">
                                    {t("hoa.payments.bcv.failed")}
                                </p>
                            ) : bcvRateQuery.data ? (
                                <div className="flex items-baseline gap-2">
                                    <span className="text-xl font-bold tabular-nums text-[--color-fg]">
                                        Bs. {bcvRateQuery.data.dollar.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                                    </span>
                                    <span className="text-xs text-[--color-muted]">
                                        {t("hoa.payments.bcv.perUsd")}
                                    </span>
                                </div>
                            ) : null}
                            {(bcvRateQuery.data?.fetched_at ?? bcvRateQuery.data?.date) && (
                                <p className="text-xs text-[--color-muted] mt-0.5">
                                    {t("hoa.payments.bcv.updated", { date: new Date(bcvRateQuery.data!.fetched_at ?? bcvRateQuery.data!.date).toLocaleString(locale, {
                                        timeZone: 'America/Caracas',
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    }) })}
                                    {bcvRateQuery.data?.source && <span className="text-[--color-muted]"> · {bcvRateQuery.data.source}</span>}
                                </p>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={() => bcvRateQuery.refetch()}
                        disabled={bcvRateQuery.isFetching}
                        className="flex items-center gap-1.5 rounded-xl border border-[--color-border] bg-gray-50 px-3 py-2 text-sm font-medium text-[--color-fg] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title={t("hoa.payments.tooltip.refreshRate")}
                    >
                        <RefreshIcon className={`h-4 w-4 ${bcvRateQuery.isFetching ? 'animate-spin' : ''}`} />
                        {bcvRateQuery.isFetching ? t("hoa.payments.bcv.refreshing") : t("hoa.payments.bcv.refresh")}
                    </button>
                </div>
            </div>

            {/* Search + filters, one block. They narrow the same list, so
                splitting them across two rows with different control styles
                just made the page taller. */}
            <div className="space-y-2.5">
            <div className="relative">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={t("hoa.payments.searchPlaceholder")}
                    className="h-10 w-full rounded-xl bg-white pl-9 pr-9 text-sm text-gray-900 ring-1 ring-gray-200 transition placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1F3A93]"
                />
                {q && (
                    <button
                        onClick={() => setQ("")}
                        aria-label={t("common.reset")}
                        className="absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
                <div>
                    <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
                        {t("hoa.payments.period")}
                    </label>
                    <select
                        value={period}
                        onChange={(e) =>
                            setPeriod(e.target.value as "THIS" | "LAST" | "ALL")
                        }
                        className={`mt-1 h-10 w-full rounded-xl px-3 text-sm ring-1 transition focus:outline-none focus:ring-2 focus:ring-[#1F3A93] ${
                            period === "THIS"
                                ? "bg-white text-gray-700 ring-gray-200"
                                : "bg-[#E7EBF8] font-medium text-[#1B3480] ring-[#C3CDEC]"
                        }`}
                    >
                        <option value="THIS">{t("hoa.payments.period.this")}</option>
                        <option value="LAST">{t("hoa.payments.period.last")}</option>
                        <option value="ALL">{t("hoa.payments.period.all")}</option>
                    </select>
                </div>
                <div>
                    <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">{t("hoa.payments.status")}</label>
                    <select
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value as "ALL" | InvoiceStatus);
                            setIncomingOnly(false);
                            // Independent dimensions, so the two can combine
                            // into an empty list with nothing on screen saying
                            // why. The dropdown is the more explicit choice.
                            setOverdueOnly(false);
                        }}
                        className={`mt-1 h-10 w-full rounded-xl px-3 text-sm ring-1 transition focus:outline-none focus:ring-2 focus:ring-[#1F3A93] ${
                            statusFilter === "ALL"
                                ? "bg-white text-gray-700 ring-gray-200"
                                : "bg-[#E7EBF8] font-medium text-[#1B3480] ring-[#C3CDEC]"
                        }`}
                    >
                        <option value="ALL">{t("hoa.payments.status.all")}</option>
                        <option value="DRAFT">{t("hoa.payments.status.draft")}</option>
                        <option value="SENT">{t("hoa.payments.status.sent")}</option>
                        <option value="UNPAID">{t("hoa.payments.status.unpaid")}</option>
                        <option value="PARTIALLY_PAID">{t("hoa.payments.status.partiallyPaid")}</option>
                        <option value="PENDING_VERIFICATION">{t("hoa.payments.status.pendingVerification")}</option>
                        <option value="PENDING_HOA_TRANSFER">{t("hoa.payments.status.pendingHoaTransfer")}</option>
                        <option value="PAID">{t("hoa.payments.status.paid")}</option>
                        <option value="CANCELLED">{t("hoa.payments.status.cancelled")}</option>
                    </select>
                </div>
                <button
                    type="button"
                    onClick={() => {
                        setPeriod("THIS");
                        setStatusFilter("ALL");
                        setOverdueOnly(false);
                        setIncomingOnly(false);
                    }}
                    className="h-10 self-end rounded-xl bg-white px-4 text-sm text-gray-600 ring-1 ring-gray-200 transition hover:bg-gray-50 hover:text-gray-900"
                >
                    {t("common.reset")}
                </button>
            </div>
            </div>

            {/* List (mobile + desktop) */}
            <div className="space-y-3 lg:space-y-0 lg:overflow-hidden lg:rounded-3xl lg:bg-white/80 lg:ring-1 lg:ring-black/5 lg:shadow-sm lg:shadow-black/[0.03] lg:backdrop-blur-xl">
                {/* mobile cards */}
                <div className="space-y-2.5 lg:hidden">
                    {listQuery.isLoading ? (
                        <PaymentListSkeleton rows={4} />
                    ) : rows.length === 0 ? (
                        <PaymentEmptyState
                            title={t("hoa.payments.noResults")}
                            hint={
                                period !== "ALL" || statusFilter !== "ALL" || overdueOnly || incomingOnly
                                    ? t("hoa.payments.noResultsFiltered")
                                    : undefined
                            }
                            action={
                                period !== "ALL" || statusFilter !== "ALL" || overdueOnly || incomingOnly ? (
                                    <button
                                        onClick={() => { setPeriod("ALL"); setStatusFilter("ALL"); setOverdueOnly(false); setIncomingOnly(false); }}
                                        className="rounded-xl bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
                                    >
                                        {t("common.reset")}
                                    </button>
                                ) : undefined
                            }
                        />
                    ) : (
                        rows.map((p) => (
                            <PaymentCard
                                key={p.id}
                                p={p}
                                onDetails={() =>
                                    isAwaitingHoaConfirmation(p)
                                        ? setIncomingDetails(p.raw)
                                        : setDetails(p)
                                }
                                onFinalize={() =>
                                    finalizeMutation.mutate({ id: p.id })
                                }
                                onDelete={() => setConfirmDelete(p)}
                                onViewProofs={() => setProofsPayment(p)}
                                bcvRate={bcvRateQuery.data?.dollar}
                            />
                        ))
                    )}
                </div>

                {/* desktop table */}
                <div className="hidden lg:block">
                    <table className="min-w-full text-sm">
                        <thead className="border-b border-black/5 bg-gray-50/70 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
                            <tr>
                                <th className="px-4 py-3">{t("hoa.payments.table.unit")}</th>
                                <th className="px-4 py-3">{t("hoa.payments.table.resident")}</th>
                                <th className="px-4 py-3">{t("hoa.payments.table.period")}</th>
                                <SortableTH
                                    label={t("hoa.payments.table.amount")}
                                    active={sortBy === "amount"}
                                    dir={sortDir}
                                    onClick={() => toggleSort("amount")}
                                />
                                <th className="px-4 py-3">{t("hoa.payments.table.status")}</th>
                                <th className="px-4 py-3">{t("hoa.payments.form.paymentMethod")}</th>
                                <SortableTH
                                    label={t("hoa.payments.table.dueDate")}
                                    active={sortBy === "dueDate"}
                                    dir={sortDir}
                                    onClick={() => toggleSort("dueDate")}
                                />
                                <SortableTH
                                    label={t("hoa.payments.table.updated")}
                                    active={sortBy === "updatedAt"}
                                    dir={sortDir}
                                    onClick={() => toggleSort("updatedAt")}
                                />
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {listQuery.isLoading ? (
                                <tr>
                                    <td colSpan={9} className="p-4">
                                        <PaymentListSkeleton rows={5} />
                                    </td>
                                </tr>
                            ) : rows.length === 0 ? (
                                <tr>
                                    {/* Was a bare "No results" string: no reason
                                        and no way out. A filter that empties the
                                        list and then offers nothing reads as the
                                        control being broken -- which is exactly
                                        what the overdue tile would now do on a
                                        month with nothing overdue. */}
                                    <td colSpan={9} className="px-4 py-10">
                                        <PaymentEmptyState
                                            title={t("hoa.payments.noResults")}
                                            hint={
                                                period !== "ALL" || statusFilter !== "ALL" || overdueOnly || incomingOnly
                                                    ? t("hoa.payments.noResultsFiltered")
                                                    : undefined
                                            }
                                            action={
                                                period !== "ALL" || statusFilter !== "ALL" || overdueOnly || incomingOnly ? (
                                                    <button
                                                        onClick={() => {
                                                            setPeriod("ALL");
                                                            setStatusFilter("ALL");
                                                            setOverdueOnly(false);
                                                        }}
                                                        className="rounded-xl bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
                                                    >
                                                        {t("common.reset")}
                                                    </button>
                                                ) : undefined
                                            }
                                        />
                                    </td>
                                </tr>
                            ) : (
                                rows.map((p) => {
                                    const canFinalize = isAwaitingHoaConfirmation(p);
                                    return (
                                        <tr
                                            key={p.id}
                                            className={`border-t border-black/5 transition hover:bg-gray-50/70 ${
                                                isSettled(p.status) && !canFinalize ? "opacity-[0.72] hover:opacity-100" : ""
                                            }`}
                                        >
                                            {/* The status colour reaches the table too, as a
                                                rail on the first cell — otherwise the desktop
                                                view is a wall of grey text and only the phone
                                                gets the scannable version. */}
                                            <td className="relative py-3 pl-5 pr-4 font-medium text-gray-900">
                                                <span
                                                    className="absolute inset-y-1.5 left-1.5 w-1 rounded-full"
                                                    style={{
                                                        background: isOverdue(p.status, p.due_date, p.is_overdue)
                                                            ? OVERDUE_TONE.rail
                                                            : WAITING_TONE[waitingOn(p.status, canFinalize)].rail,
                                                    }}
                                                    aria-hidden
                                                />
                                                {p.unitNumber}
                                            </td>
                                            <td className="px-4 py-3 text-gray-700">
                                                {p.residentName}
                                            </td>
                                            <td className="px-4 py-3 tabular-nums text-gray-600">
                                                {fmtPeriod(p.period)}
                                            </td>
                                            <td className="px-4 py-3">
                                                <Money
                                                    usd={p.total_amount}
                                                    ves={
                                                        (p.bcv_rate_snapshot ?? bcvRateQuery.data?.dollar)
                                                            ? p.total_amount * (p.bcv_rate_snapshot ?? bcvRateQuery.data!.dollar)
                                                            : null
                                                    }
                                                    align="left"
                                                    size="sm"
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    <StatusBadge status={p.status} />
                                                    {isOverdue(p.status, p.due_date, p.is_overdue) && (
                                                        <OverdueChip
                                                            days={Math.floor(
                                                                (Date.now() - new Date(p.due_date).getTime()) / 86_400_000
                                                            )}
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-gray-600">
                                                {prettyMethod(p.payment_method, t)}
                                            </td>
                                            <td className="px-4 py-3 tabular-nums text-gray-600">
                                                {fmtDate(p.due_date)}
                                            </td>
                                            <td className="px-4 py-3 tabular-nums text-gray-500">
                                                {new Date(
                                                    p.updated_at
                                                ).toLocaleDateString()}
                                            </td>
                                            <td className="px-4 py-3">
                                                <RowActions
                                                    p={p}
                                                    onDetails={() =>
                                                        isAwaitingHoaConfirmation(p)
                                                            ? setIncomingDetails(p.raw)
                                                            : setDetails(p)
                                                    }
                                                    onViewProofs={() =>
                                                        setProofsPayment(p)
                                                    }
                                                    onFinalize={() =>
                                                        finalizeMutation.mutate(
                                                            { id: p.id }
                                                        )
                                                    }
                                                    onDelete={() => {
                                                        setConfirmDelete(p);
                                                    }}
                                                    canFinalize={canFinalize}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
                </>
            )}

            {/* Tab Content: Notification Log */}
            {activeTab === "notifications" && (
                <NotificationLog
                    rows={notifLogQuery.data ?? []}
                    isLoading={notifLogQuery.isLoading}
                    isFetching={notifLogQuery.isFetching}
                    onRefresh={() => notifLogQuery.refetch()}
                    typeFilter={notifTypeFilter}
                    setTypeFilter={setNotifTypeFilter}
                    statusFilter={notifStatusFilter}
                    setStatusFilter={setNotifStatusFilter}
                />
            )}

            {/* Create single invoice */}
            {createOpen &&
                residentsQuery.data &&
                unitsQuery.data &&
                condosQuery.data && (
                    <CreateInvoiceModal
                        residents={residentsQuery.data}
                        units={unitsQuery.data}
                        condos={condosQuery.data}
                        onClose={() => setCreateOpen(false)}
                        onSubmit={(data) => createMutation.mutate(data)}
                        submitting={createMutation.isPending}
                        bcvRate={bcvRateQuery.data?.dollar}
                    />
                )}

            {/* Bulk generate */}
            {bulkOpen &&
                residentsQuery.data &&
                unitsQuery.data &&
                condosQuery.data && (
                    <BulkGenerateModal
                        residents={residentsQuery.data}
                        units={unitsQuery.data}
                        condos={condosQuery.data}
                        onClose={() => setBulkOpen(false)}
                        onSubmit={(payload) =>
                            bulkMutation.mutate({
                                resident_ids: payload.resident_ids,
                                period: payload.period,
                                due_date: payload.due_date,
                                payment_method: payload.payment_method,
                                notes: payload.notes?.trim(),
                                tenant_only: payload.tenant_only,
                                use_unit_fee: payload.use_unit_fee,
                                fixed_amount: payload.use_unit_fee
                                    ? undefined
                                    : payload.fixed_amount,
                            })
                        }
                        submitting={bulkMutation.isPending}
                        bcvRate={bcvRateQuery.data?.dollar}
                    />
                )}

            {/* Details */}
            {details && (
                <DetailsModal
                    p={details}
                    onClose={() => setDetails(null)}
                    bcvRate={bcvRateQuery.data?.dollar}
                    onViewRate={(date) => {
                        setDetails(null);
                        setRateDayFocus(date);
                        setActiveTab("rates");
                    }}
                />
            )}

            {/* Incoming Payment Details */}
            {incomingDetails && (
                <IncomingPaymentDetailsModal
                    invoice={incomingDetails}
                    onClose={() => setIncomingDetails(null)}
                    onConfirm={(id) => finalizeMutation.mutate({ id })}
                    isConfirming={finalizeMutation.isPending}
                    bcvRate={bcvRateQuery.data?.dollar}
                />
            )}

            {reminderOpen && (
                <OverdueReminderModal
                    onClose={() => setReminderOpen(false)}
                    onSent={(result) => {
                        setReminderOpen(false);
                        setBulkReminderResult(result);
                        toast.success(
                            t("hoa.payments.bulkReminderSummary", {
                                total: result.total,
                                sent: result.sent,
                                mocked: result.mocked,
                            })
                        );
                    }}
                />
            )}

            {/* Payment Proofs Modal */}
            {proofsPayment && (
                <PaymentProofsModal
                    payment={proofsPayment}
                    onClose={() => setProofsPayment(null)}
                />
            )}
        </div>
    );
}

/** =========================
 * Components
 * ========================= */
function PaymentCard({
    p,
    onDetails,
    onFinalize,
    onDelete,
    onViewProofs,
    bcvRate,
}: {
    p: Payment;
    onDetails: () => void;
    onFinalize: () => void;
    onDelete: () => void;
    onViewProofs?: () => void;
    bcvRate?: number;
}) {
    const { t } = useLanguage();
    const labels = usePaymentLabels();
    const canFinalize = isAwaitingHoaConfirmation(p);
    const state = getHoaPaymentState(p, t, useLanguage().language);

    const overdue = isOverdue(p.status, p.due_date, p.is_overdue);
    const overdueDays = overdue
        ? Math.floor((Date.now() - new Date(p.due_date).getTime()) / 86_400_000)
        : 0;
    const rate = p.bcv_rate_snapshot ?? bcvRate;

    return (
        <PaymentCardShell
            status={p.status}
            overdue={overdue}
            awaitingConfirmation={canFinalize}
            // An invoice awaiting YOUR confirmation is not finished business,
            // so it must not fade out with the settled ones.
            settled={isSettled(p.status) && !canFinalize}
        >
            <button
                onClick={onDetails}
                className="flex w-full items-start gap-3 px-3.5 py-3.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#1F3A93]"
            >
                <StatusWell status={p.status} overdue={overdue} />

                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                        <h3 className="min-w-0 truncate text-[15px] font-semibold text-gray-900">
                            {t("hoa.payments.card.unit", { unit: p.unitNumber })}
                        </h3>
                        <Money usd={p.total_amount} ves={rate ? p.total_amount * rate : null} />
                    </div>

                    <p className="mt-0.5 truncate text-[13px] text-gray-600">{p.residentName}</p>

                    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5">
                        <PayStatusChip status={p.status} labels={labels} size="sm" />
                        {overdue && <OverdueChip days={overdueDays} />}
                        <QuietChip>{fmtPeriod(p.period)}</QuietChip>
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-gray-500">
                        <WaitingLabel
                            status={p.status}
                            labels={labels}
                            awaitingConfirmation={canFinalize}
                        />
                        <span>{t("hoa.payments.card.due", { date: fmtDate(p.due_date) })}</span>
                    </div>

                    {state.subtitle && (
                        <p className="mt-1.5 text-[11px] leading-relaxed text-gray-500">
                            {state.subtitle}
                        </p>
                    )}
                </div>
            </button>

            <div className="-mx-0 flex items-center gap-1.5 overflow-x-auto border-t border-black/[0.06] px-3.5 py-2">
                <RowActions
                    p={p}
                    onDetails={onDetails}
                    onFinalize={onFinalize}
                    onDelete={onDelete}
                    onViewProofs={onViewProofs}
                    canFinalize={canFinalize}
                />
            </div>
        </PaymentCardShell>
    );
}

function RowActions({
    p,
    onDetails,
    onFinalize,
    onDelete,
    onViewProofs,
    canFinalize,
}: {
    p: Payment;
    onDetails: () => void;
    onFinalize: () => void;
    onDelete: () => void;
    onViewProofs?: () => void;
    canFinalize: boolean;
}) {
    const { t } = useLanguage();
    const queryClient = useQueryClient();
    // Can only delete draft, sent, or cancelled invoices (not paid ones)
    const canDelete = p.status === "DRAFT" || p.status === "SENT" || p.status === "CANCELLED";
    const canRemind = p.status === "SENT" || p.status === "UNPAID";
    // Direct-HOA pending reviews happen inside the Details modal (approve/reject there).
    const canViewProofs =
        (p.payment_flow === "PLATFORM_RELAY" &&
            (p.status === "PAID" ||
                p.status === "PENDING_HOA_TRANSFER" ||
                p.status === "PENDING_VERIFICATION")) ||
        (p.payment_flow === "DIRECT_HOA" && p.status === "PAID");

    const reminderMut = useMutation({
        mutationFn: () => sendOverdueReminder(p.id),
        onSuccess: () => {
            toast.success(t("invoice.reminderSent"));
            queryClient.invalidateQueries({ queryKey: ["invoice-detail", p.id] });
        },
        onError: () => toast.error(t("invoice.reminderFailed")),
    });
    
    const menu = [
        ...(canViewProofs && onViewProofs
            ? [{ key: "proofs", label: t("hoa.payments.viewProofs"), onClick: onViewProofs, icon: ImageIcon }]
            : []),
        ...(canRemind
            ? [{
                  key: "remind",
                  label: t("invoice.sendReminder"),
                  onClick: () => reminderMut.mutate(),
                  disabled: reminderMut.isPending,
              }]
            : []),
        ...(canFinalize
            ? [{ key: "finalize", label: t("hoa.payments.finalize"), onClick: onFinalize }]
            : []),
        ...(canDelete
            ? [{ key: "delete", label: t("hoa.payments.delete"), onClick: onDelete, tone: "danger" as const }]
            : []),
    ];

    return (
        <div className="flex items-center justify-end gap-1.5">
            {/* One quiet primary, everything else behind the menu. Four
                buttons in a table cell wrapped onto three lines and gave
                every row a different height. */}
            <button
                className="shrink-0 rounded-lg bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 ring-1 ring-gray-200 transition hover:bg-gray-50"
                onClick={onDetails}
                title={t("hoa.payments.tooltip.viewDetails")}
            >{t("hoa.payments.viewDetails")}</button>
            <RowActionMenu items={menu} label={t("hoa.payments.moreActions")} />
        </div>
    );
}

/** Stat card (inline, from your style) */

/** Sortable TH */
function SortableTH({
    label,
    active,
    dir,
    onClick,
}: {
    label: string;
    active: boolean;
    dir: "asc" | "desc";
    onClick: () => void;
}) {
    return (
        <th className="px-4 py-3">
            <button
                onClick={onClick}
                className="inline-flex items-center gap-1 text-[12px] uppercase tracking-wide text-[--color-muted] hover:text-[color:var(--color-fg)]"
            >
                {label}
                <span
                    className={`transition ${
                        active ? "opacity-100" : "opacity-30"
                    }`}
                >
                    {dir === "asc" ? "▲" : "▼"}
                </span>
            </button>
        </th>
    );
}

function StatusBadge({ status }: { status: InvoiceStatus }) {
    // Delegates to the shared chip so the badge cannot drift from the cards.
    // The old map gave DRAFT, SENT, UNPAID and PENDING_HOA_TRANSFER the same
    // blue and PARTIALLY_PAID the same amber as PENDING_VERIFICATION, so in a
    // list of forty invoices the colour told you almost nothing.
    const labels = usePaymentLabels();
    return <PayStatusChip status={status} labels={labels} size="sm" />;
}

/** =========================
 * Modals
 * ========================= */
/**
 * Create one invoice.
 *
 * What this replaces: three overlapping controls for a single decision. A
 * search box, a resident dropdown and a unit dropdown, each with its own
 * onChange writing the other two, so picking a resident cleared the search
 * you had just typed and picking a unit rewrote the resident. Choosing one
 * person needed you to understand which of three widgets currently owned the
 * answer.
 *
 * Now: building, then ONE picker over residents and units together, then the
 * money. The same shape as the issues form, and the same TargetPicker idea --
 * you search for "Maria", not for "B-204".
 *
 * Fifteen console.log calls are gone with it, one of which ran per resident
 * inside a filter and logged the whole directory on every keystroke.
 */
function CreateInvoiceModal({
    residents,
    units,
    condos,
    onClose,
    onSubmit,
    submitting,
    bcvRate,
}: {
    residents: ResidentList[];
    units: Unit[];
    condos: Condo[];
    onClose: () => void;
    onSubmit: (data: CreateInvoiceData) => void;
    submitting: boolean;
    bcvRate?: number;
}) {
    const { t, language } = useLanguage();
    const locale = language === "es" ? "es-VE" : "en-US";

    const [buildingId, setBuildingId] = React.useState<string>(
        condos.length === 1 ? condos[0].id : ""
    );
    const [residentId, setResidentId] = React.useState("");
    const [unitId, setUnitId] = React.useState("");
    const [query, setQuery] = React.useState("");

    const [period, setPeriod] = React.useState(THIS_YYYYMM);
    // No default figure. It used to open at $120, which is not this
    // building's fee and is exactly the kind of plausible-looking number that
    // gets sent without being read.
    const [amount, setAmount] = React.useState<number>(0);
    const [amountVes, setAmountVes] = React.useState<number>(0);
    const [dueDate, setDueDate] = React.useState(
        `${NOW.getFullYear()}-${String(NOW.getMonth() + 1).padStart(2, "0")}-10`
    );
    const [method, setMethod] = React.useState<PaymentMethod | "">("");
    const [notes, setNotes] = React.useState("");

    const selectedBuilding = condos.find((c) => c.id === buildingId);

    /* ---- who lives where ------------------------------------------ *
     * Residents carry `unit_building` as the building's NAME, so this has
     * to match on name. That is the server's shape, not a choice made here,
     * and it is why two buildings sharing a name would cross-contaminate --
     * worth fixing upstream, out of scope for a form redesign.
     * --------------------------------------------------------------- */
    const buildingResidents = React.useMemo(() => {
        if (!selectedBuilding) return [];
        const name = (selectedBuilding.name || "").trim();
        return residents.filter((r) => (r.unit_building || "").trim() === name);
    }, [selectedBuilding, residents]);

    const buildingUnits = React.useMemo(
        () => (buildingId ? units.filter((u) => u.building_id === buildingId) : []),
        [buildingId, units]
    );

    /** One row per unit, with whoever lives in it. The picker's whole list. */
    const targets = React.useMemo(() => {
        const byUnitCode = new Map<string, ResidentList[]>();
        for (const r of buildingResidents) {
            const code = r.unit_code || "";
            byUnitCode.set(code, [...(byUnitCode.get(code) ?? []), r]);
        }
        return buildingUnits.map((u) => {
            const code = u.unit_code || u.number || "";
            return {
                unit: u,
                code,
                fee: u.monthly_fee ? parseFloat(u.monthly_fee) : null,
                people: byUnitCode.get(code) ?? [],
            };
        });
    }, [buildingUnits, buildingResidents]);

    const matches = React.useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return targets;
        return targets.filter(
            (row) =>
                row.code.toLowerCase().includes(q) ||
                row.people.some(
                    (p) =>
                        (p.full_name || "").toLowerCase().includes(q) ||
                        (p.email || "").toLowerCase().includes(q)
                )
        );
    }, [targets, query]);

    const chosen = targets.find((row) => row.unit.id === unitId) ?? null;

    /** Picking a unit sets the fee and the person, in one action. */
    function choose(row: (typeof targets)[number], person?: ResidentList) {
        setUnitId(row.unit.id);
        const who = person ?? row.people[0];
        setResidentId(who?.id ?? "");
        if (row.fee !== null) {
            setAmount(row.fee);
            if (bcvRate) setAmountVes(row.fee * bcvRate);
        }
    }

    function changeBuilding(next: string) {
        setBuildingId(next);
        setUnitId("");
        setResidentId("");
        setQuery("");
    }

    /* ---- money ----------------------------------------------------- *
     * Two-way, and neither side is derived state: typing in bolívares must
     * not be silently overwritten by a USD effect on the next render.
     * --------------------------------------------------------------- */
    function changeUsd(value: number) {
        setAmount(value);
        if (bcvRate) setAmountVes(value * bcvRate);
    }
    function changeVes(value: number) {
        setAmountVes(value);
        if (bcvRate) setAmount(value / bcvRate);
    }
    React.useEffect(() => {
        if (bcvRate) setAmountVes(amount * bcvRate);
        // Only when the RATE arrives -- not on every amount change, which
        // would fight the VES input mid-typing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bcvRate]);

    const amountOk = amount > 0;
    const canSubmit = !!buildingId && !!unitId && amountOk && !!period && !!dueDate;

    function submit(e: React.FormEvent) {
        e.preventDefault();
        if (!canSubmit) return;
        onSubmit({
            resident: residentId || null,
            unit: unitId || null,
            period,
            base_amount: Number(amount),
            due_date: dueDate,
            payment_method: method || null,
            notes: notes.trim() || undefined,
        });
    }

    return (
        <ResponsiveModal
            open
            onClose={onClose}
            title={t("hoa.payments.modal.createTitle")}
            subtitle={t("hoa.payments.form.createSubtitle")}
            headerAccent={BRAND}
            size="lg"
            footer={
                <FormFooter
                    form="create-invoice"
                    onCancel={onClose}
                    cancelLabel={t("common.cancel")}
                    submitLabel={t("common.create")}
                    busyLabel={t("hoa.payments.form.creating")}
                    busy={submitting}
                    disabled={!canSubmit}
                />
            }
        >
            <form id="create-invoice" onSubmit={submit} className="space-y-3">
                <FormPanel title={t("hoa.payments.form.section.who")}>
                    {condos.length > 1 && (
                        <FormField label={t("hoa.payments.bulk.building")} required>
                            <select
                                value={buildingId}
                                onChange={(e) => changeBuilding(e.target.value)}
                                className={fieldClass}
                            >
                                <option value="">{t("hoa.payments.bulk.selectBuilding")}</option>
                                {condos.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </FormField>
                    )}

                    {!buildingId ? (
                        <p className="rounded-xl bg-gray-50 px-3 py-4 text-center text-[13px] text-gray-500">
                            {t("hoa.payments.form.pickBuildingFirst")}
                        </p>
                    ) : (
                        <FormField
                            label={t("hoa.payments.form.unitOrResident")}
                            required
                            hint={chosen ? undefined : t("hoa.payments.form.pickOneHint")}
                        >
                            <div className="relative">
                                <Search
                                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                                    aria-hidden
                                />
                                <input
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder={t("hoa.payments.form.searchResidentByNameEmailUnit")}
                                    className={`${fieldClass} pl-9`}
                                />
                            </div>

                            {matches.length === 0 ? (
                                <p className="mt-2 rounded-xl bg-gray-50 px-3 py-4 text-center text-[13px] text-gray-500">
                                    {targets.length === 0
                                        ? t("hoa.payments.form.noUnitsInBuilding")
                                        : t("hoa.payments.form.noMatches")}
                                </p>
                            ) : (
                                <ul
                                    role="listbox"
                                    className="mt-2 max-h-56 space-y-1 overflow-y-auto rounded-xl bg-gray-50 p-1 ring-1 ring-gray-200"
                                >
                                    {matches.map((row) => {
                                        const on = row.unit.id === unitId;
                                        return (
                                            <li key={row.unit.id}>
                                                <button
                                                    type="button"
                                                    role="option"
                                                    aria-selected={on}
                                                    onClick={() => choose(row)}
                                                    className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93] ${
                                                        on ? "bg-white shadow-sm" : "hover:bg-white/70"
                                                    }`}
                                                    style={on ? { boxShadow: `inset 0 0 0 1.5px ${BRAND}` } : undefined}
                                                >
                                                    <span
                                                        className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[11px] font-bold ${
                                                            on
                                                                ? "bg-[#E7EBF8] text-[#1B3480]"
                                                                : "bg-white text-gray-500 ring-1 ring-gray-200"
                                                        }`}
                                                    >
                                                        {row.code.split("-").pop()}
                                                    </span>
                                                    <span className="min-w-0 flex-1">
                                                        <span className="block truncate text-[13px] font-semibold text-gray-900">
                                                            {row.code}
                                                        </span>
                                                        <span className="block truncate text-[11px] text-gray-500">
                                                            {row.people.length
                                                                ? row.people.map((p) => p.full_name).join(", ")
                                                                : t("hoa.payments.form.vacantUnit")}
                                                        </span>
                                                    </span>
                                                    {row.fee !== null && (
                                                        <span className="shrink-0 text-[11px] tabular-nums text-gray-500">
                                                            {fmtMoney(row.fee)}
                                                        </span>
                                                    )}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}

                            {/* More than one person on the unit: say which the
                                invoice is addressed to, rather than silently
                                taking the first. */}
                            {chosen && chosen.people.length > 1 && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {chosen.people.map((p) => (
                                        <button
                                            key={p.id}
                                            type="button"
                                            onClick={() => choose(chosen, p)}
                                            aria-pressed={residentId === p.id}
                                            className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition ${
                                                residentId === p.id
                                                    ? "bg-[#E7EBF8] text-[#1B3480]"
                                                    : "bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50"
                                            }`}
                                        >
                                            {p.full_name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </FormField>
                    )}
                </FormPanel>

                <FormPanel title={t("hoa.payments.form.section.amount")}>
                    <FormField
                        label={t("common.amount")}
                        required
                        hint={
                            bcvRate
                                ? t("hoa.payments.bcv.rateLine", {
                                      rate: bcvRate.toLocaleString(locale, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 4,
                                      }),
                                  })
                                : t("hoa.payments.bcv.unavailable")
                        }
                    >
                        <div className="grid grid-cols-2 gap-2">
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">$</span>
                                <input
                                    type="number"
                                    inputMode="decimal"
                                    min={0}
                                    step="0.01"
                                    value={amount ? amount.toFixed(2) : ""}
                                    placeholder="0.00"
                                    onChange={(e) => changeUsd(Number(e.target.value))}
                                    className={`${fieldClass} pl-7 pr-12`}
                                    required
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-400">USD</span>
                            </div>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">Bs.</span>
                                <input
                                    type="number"
                                    inputMode="decimal"
                                    min={0}
                                    step="0.01"
                                    value={amountVes ? amountVes.toFixed(2) : ""}
                                    placeholder="0.00"
                                    onChange={(e) => changeVes(Number(e.target.value))}
                                    className={`${fieldClass} pl-9 pr-12`}
                                    disabled={!bcvRate}
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-400">VES</span>
                            </div>
                        </div>
                    </FormField>

                    <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                        <FormField label={t("hoa.payments.form.period")} required>
                            <input
                                type="month"
                                value={period}
                                onChange={(e) => setPeriod(e.target.value)}
                                className={fieldClass}
                                required
                            />
                        </FormField>
                        <FormField label={t("hoa.payments.form.dueDate")} required>
                            <input
                                type="date"
                                value={dueDate}
                                onChange={(e) => setDueDate(e.target.value)}
                                className={fieldClass}
                                required
                            />
                        </FormField>
                    </div>
                </FormPanel>

                <FormPanel title={t("common.optional")}>
                    <FormField label={t("hoa.payments.form.paymentMethod")}>
                        <select
                            value={method}
                            onChange={(e) => setMethod(e.target.value as PaymentMethod | "")}
                            className={fieldClass}
                        >
                            <option value="">{t("hoa.payments.form.methodUnset")}</option>
                            <option value="STRIPE">{t("hoa.payments.form.paymentMethod.stripe")}</option>
                            <option value="BANK_TRANSFER">{t("hoa.payments.form.paymentMethod.bank")}</option>
                            <option value="ZELLE">{t("hoa.payments.form.paymentMethod.zelle")}</option>
                            <option value="PAGO_MOVIL">{t("hoa.payments.form.paymentMethod.pagoMovil")}</option>
                            <option value="CASH">{t("hoa.payments.form.paymentMethod.cash")}</option>
                        </select>
                    </FormField>
                    <FormField label={t("hoa.payments.form.notes")}>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            className={areaClass}
                        />
                    </FormField>
                </FormPanel>
            </form>
        </ResponsiveModal>
    );
}

/**
 * Generate many invoices at once.
 *
 * The order was backwards. Every setting came first and the recipient list --
 * the entire point of a bulk action -- was last, in a 144px scroll box below
 * the fold, with "select all" as a small text link beside it. You configured
 * an invoice run before seeing who it was for.
 *
 * Now: who, then how much, then when. The list leads, it is tall enough to
 * read, and the count is stated where you are looking rather than only in
 * the footer.
 *
 * Twenty-one console.log calls are gone, along with a `mode` state that was
 * set but never read -- a dead "ALL_ACTIVE vs SELECT" switch whose ALL_ACTIVE
 * branch could still be reached by submit() and would have invoiced every
 * visible resident regardless of the checkboxes.
 */
function BulkGenerateModal({
    residents,
    units,
    condos,
    onClose,
    onSubmit,
    submitting,
    bcvRate,
}: {
    residents: ResidentList[];
    units: Unit[];
    condos: Condo[];
    onClose: () => void;
    onSubmit: (payload: {
        resident_ids: string[];
        period: string;
        due_date: string;
        payment_method?: PaymentMethod;
        notes?: string;
        tenant_only: boolean;
        use_unit_fee: boolean;
        fixed_amount?: number;
    }) => void;
    submitting: boolean;
    bcvRate?: number;
}) {
    const { t, language } = useLanguage();
    const locale = language === "es" ? "es-VE" : "en-US";

    const [buildingFilter, setBuildingFilter] = React.useState<string>(
        condos.length === 1 ? condos[0].id : "ALL"
    );
    const [amountSource, setAmountSource] = React.useState<AmountSource>("UNIT_RENT");
    const [period, setPeriod] = React.useState(THIS_YYYYMM);
    const [amount, setAmount] = React.useState<number>(0);
    const [amountVes, setAmountVes] = React.useState<number>(0);
    const [dueDate, setDueDate] = React.useState(() => {
        const d = new Date(NOW.getFullYear(), NOW.getMonth(), 10);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
            d.getDate()
        ).padStart(2, "0")}`;
    });
    const [method, setMethod] = React.useState<PaymentMethod | "">("");
    const [notes, setNotes] = React.useState("");
    const [q, setQ] = React.useState("");
    const [selected, setSelected] = React.useState<Set<string>>(new Set());

    /** Unit fees, so the list can show what each resident would be charged. */
    const feeByUnitCode = React.useMemo(() => {
        const map = new Map<string, number>();
        for (const u of units) {
            const code = u.unit_code || u.number;
            if (code && u.monthly_fee) map.set(code, parseFloat(u.monthly_fee));
        }
        return map;
    }, [units]);

    const selectable = React.useMemo(() => {
        let list = residents.slice();

        if (buildingFilter !== "ALL") {
            const condo = condos.find((c) => c.id === buildingFilter);
            if (condo) {
                // Residents carry the building's NAME, not its id. The
                // server's shape, not a choice made here.
                const name = (condo.name || "").trim();
                list = list.filter((r) => (r.unit_building || "").trim() === name);
            }
        }

        const s = q.trim().toLowerCase();
        if (s) {
            list = list.filter(
                (r) =>
                    r.full_name.toLowerCase().includes(s) ||
                    (r.unit_code || "").toLowerCase().includes(s) ||
                    (r.email || "").toLowerCase().includes(s)
            );
        }
        return list;
    }, [residents, buildingFilter, condos, q]);

    // Anything selected then filtered out of view would still be invoiced,
    // which is money going somewhere the admin can no longer see. Keep the
    // selection to what is actually selectable.
    const visibleIds = React.useMemo(
        () => new Set(selectable.map((r) => r.id)),
        [selectable]
    );
    const effective = React.useMemo(
        () => [...selected].filter((id) => visibleIds.has(id)),
        [selected, visibleIds]
    );

    const allVisibleChosen =
        selectable.length > 0 && effective.length === selectable.length;

    function toggle(id: string) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    function changeUsd(value: number) {
        setAmount(value);
        if (bcvRate) setAmountVes(value * bcvRate);
    }
    function changeVes(value: number) {
        setAmountVes(value);
        if (bcvRate) setAmount(value / bcvRate);
    }

    /** What this run will actually bill, so the total is visible before the click. */
    const projected = React.useMemo(() => {
        if (amountSource === "FIXED") return amount * effective.length;
        return effective.reduce((sum, id) => {
            const r = selectable.find((x) => x.id === id);
            return sum + (feeByUnitCode.get(r?.unit_code || "") ?? 0);
        }, 0);
    }, [amountSource, amount, effective, selectable, feeByUnitCode]);

    const amountOk = amountSource === "UNIT_RENT" || amount > 0;
    const canSubmit = effective.length > 0 && amountOk && !!period && !!dueDate;

    function submit(e: React.FormEvent) {
        e.preventDefault();
        if (!canSubmit) return;
        onSubmit({
            resident_ids: effective,
            period,
            due_date: dueDate,
            payment_method: (method || undefined) as PaymentMethod | undefined,
            notes: notes.trim() || undefined,
            tenant_only: false,
            use_unit_fee: amountSource === "UNIT_RENT",
            fixed_amount: amountSource === "FIXED" ? Number(amount) : undefined,
        });
    }

    return (
        <ResponsiveModal
            open
            onClose={onClose}
            size="lg"
            title={t("hoa.payments.modal.bulkTitle")}
            subtitle={t("hoa.payments.bulk.subtitle")}
            headerAccent={BRAND}
            footer={
                <div className="space-y-2">
                    {/* The consequence, stated where the button is. */}
                    <div className="flex items-center justify-between gap-3 text-[12px]">
                        <span className="text-gray-600">
                            {t("hoa.payments.bulk.selectedInvoices", { count: effective.length })}
                        </span>
                        {effective.length > 0 && projected > 0 && (
                            <span className="font-semibold tabular-nums text-gray-900">
                                {t("hoa.payments.bulk.projectedTotal", { total: fmtMoney(projected) })}
                            </span>
                        )}
                    </div>
                    <FormFooter
                        form="bulk-generate"
                        onCancel={onClose}
                        cancelLabel={t("common.cancel")}
                        submitLabel={t("hoa.payments.bulk.generate")}
                        busyLabel={t("hoa.payments.bulk.generating")}
                        busy={submitting}
                        disabled={!canSubmit}
                    />
                </div>
            }
        >
            <form id="bulk-generate" onSubmit={submit} className="space-y-3">
                {/* WHO — first, because it is what a bulk run is. */}
                <FormPanel title={t("hoa.payments.form.section.recipients")}>
                    {condos.length > 1 && (
                        <FormField label={t("hoa.payments.bulk.building")}>
                            <select
                                value={buildingFilter}
                                onChange={(e) => setBuildingFilter(e.target.value)}
                                className={fieldClass}
                            >
                                <option value="ALL">{t("hoa.payments.bulk.allBuildings")}</option>
                                {condos.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </FormField>
                    )}

                    <div className="relative">
                        <Search
                            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                            aria-hidden
                        />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder={t("hoa.payments.form.searchResidentByNameEmailUnit")}
                            className={`${fieldClass} pl-9`}
                        />
                    </div>

                    <div className="flex items-center justify-between gap-3 rounded-xl bg-gray-50 px-3 py-2.5 ring-1 ring-gray-200">
                        <label className="flex cursor-pointer items-center gap-2.5 text-[13px] font-medium text-gray-800">
                            <input
                                type="checkbox"
                                checked={allVisibleChosen}
                                onChange={() =>
                                    setSelected(
                                        allVisibleChosen
                                            ? new Set()
                                            : new Set(selectable.map((r) => r.id))
                                    )
                                }
                                className="h-4 w-4 rounded border-gray-300 text-[#1F3A93] focus:ring-[#1F3A93]"
                            />
                            {t("hoa.payments.reminderModal.selectAll")}
                        </label>
                        <span className="text-[12px] tabular-nums text-gray-500">
                            {t("hoa.payments.reminderModal.chosen", {
                                count: effective.length,
                                total: selectable.length,
                            })}
                        </span>
                    </div>

                    {selectable.length === 0 ? (
                        <p className="rounded-xl bg-gray-50 px-3 py-6 text-center text-[13px] text-gray-500">
                            {t("hoa.payments.bulk.noResidentsFound")}
                        </p>
                    ) : (
                        <ul className="max-h-72 space-y-1.5 overflow-y-auto">
                            {selectable.map((r) => {
                                const on = selected.has(r.id);
                                const fee = feeByUnitCode.get(r.unit_code || "");
                                return (
                                    <li key={r.id}>
                                        <label
                                            className={`flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 ring-1 transition ${
                                                on
                                                    ? "bg-white ring-[#C3CDEC]"
                                                    : "bg-gray-50/60 ring-transparent hover:bg-gray-50"
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={on}
                                                onChange={() => toggle(r.id)}
                                                className="h-4 w-4 shrink-0 rounded border-gray-300 text-[#1F3A93] focus:ring-[#1F3A93]"
                                            />
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-[14px] font-semibold text-gray-900">
                                                    {r.full_name}
                                                </span>
                                                <span className="mt-0.5 block truncate text-[11px] text-gray-500">
                                                    {r.unit_code || t("hoa.payments.form.noUnit")}
                                                    {r.email ? ` · ${r.email}` : ""}
                                                </span>
                                            </span>
                                            {/* What THIS resident would be billed, so an
                                                empty unit fee is visible before sending. */}
                                            <span className="shrink-0 text-[12px] font-semibold tabular-nums text-gray-700">
                                                {amountSource === "FIXED"
                                                    ? amount > 0
                                                        ? fmtMoney(amount)
                                                        : "—"
                                                    : fee
                                                    ? fmtMoney(fee)
                                                    : "—"}
                                            </span>
                                        </label>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </FormPanel>

                {/* HOW MUCH */}
                <FormPanel title={t("hoa.payments.form.section.amount")}>
                    <FormField label={t("hoa.payments.form.amountSourceLabel")}>
                        <div className="flex gap-1 rounded-xl bg-gray-100/80 p-1">
                            {(["UNIT_RENT", "FIXED"] as AmountSource[]).map((src) => {
                                const on = amountSource === src;
                                return (
                                    <button
                                        key={src}
                                        type="button"
                                        onClick={() => setAmountSource(src)}
                                        aria-pressed={on}
                                        className={`flex-1 rounded-lg py-2 text-[12px] font-semibold transition ${
                                            on
                                                ? "bg-white text-gray-900 shadow-sm"
                                                : "text-gray-600 hover:text-gray-900"
                                        }`}
                                    >
                                        {src === "UNIT_RENT"
                                            ? t("hoa.payments.form.amountSource.unitRent")
                                            : t("hoa.payments.form.amountSource.fixed")}
                                    </button>
                                );
                            })}
                        </div>
                    </FormField>

                    {amountSource === "UNIT_RENT" ? (
                        <p className="rounded-xl bg-[#E7EBF8]/60 px-3 py-2.5 text-[12px] leading-relaxed text-[#1B3480]">
                            {t("hoa.payments.bulk.useUnitRent")}
                        </p>
                    ) : (
                        <FormField
                            label={t("common.amount")}
                            required
                            hint={
                                bcvRate
                                    ? t("hoa.payments.bcv.rateLine", {
                                          rate: bcvRate.toLocaleString(locale, {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 4,
                                          }),
                                      })
                                    : t("hoa.payments.bcv.unavailable")
                            }
                        >
                            <div className="grid grid-cols-2 gap-2">
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">$</span>
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        min={0}
                                        step="0.01"
                                        value={amount ? amount.toFixed(2) : ""}
                                        placeholder="0.00"
                                        onChange={(e) => changeUsd(Number(e.target.value))}
                                        className={`${fieldClass} pl-7 pr-12`}
                                        required
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-400">USD</span>
                                </div>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">Bs.</span>
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        min={0}
                                        step="0.01"
                                        value={amountVes ? amountVes.toFixed(2) : ""}
                                        placeholder="0.00"
                                        onChange={(e) => changeVes(Number(e.target.value))}
                                        className={`${fieldClass} pl-9 pr-12`}
                                        disabled={!bcvRate}
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-400">VES</span>
                                </div>
                            </div>
                        </FormField>
                    )}

                    <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                        <FormField label={t("hoa.payments.bulk.period")} required>
                            <input
                                type="month"
                                value={period}
                                onChange={(e) => setPeriod(e.target.value)}
                                className={fieldClass}
                                required
                            />
                        </FormField>
                        <FormField label={t("hoa.payments.bulk.dueDate")} required>
                            <input
                                type="date"
                                value={dueDate}
                                onChange={(e) => setDueDate(e.target.value)}
                                className={fieldClass}
                                required
                            />
                        </FormField>
                    </div>
                </FormPanel>

                <FormPanel title={t("common.optional")}>
                    <FormField label={t("hoa.payments.form.paymentMethod")}>
                        <select
                            value={method}
                            onChange={(e) => setMethod(e.target.value as PaymentMethod | "")}
                            className={fieldClass}
                        >
                            <option value="">{t("hoa.payments.form.methodUnset")}</option>
                            <option value="STRIPE">{t("hoa.payments.form.paymentMethod.stripe")}</option>
                            <option value="BANK_TRANSFER">{t("hoa.payments.form.paymentMethod.bank")}</option>
                            <option value="ZELLE">{t("hoa.payments.form.paymentMethod.zelle")}</option>
                            <option value="PAGO_MOVIL">{t("hoa.payments.form.paymentMethod.pagoMovil")}</option>
                            <option value="CASH">{t("hoa.payments.form.paymentMethod.cash")}</option>
                        </select>
                    </FormField>
                    <FormField label={t("hoa.payments.form.notes")}>
                        <input
                            type="text"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder={t("hoa.payments.bulk.notesPlaceholder")}
                            className={fieldClass}
                        />
                    </FormField>
                </FormPanel>
            </form>
        </ResponsiveModal>
    );
}

function DetailsModal({ p, onClose, bcvRate, onViewRate }: { p: Payment; onClose: () => void; bcvRate?: number; onViewRate?: (date: string) => void }) {
    const { t, language } = useLanguage();
    const payLabels = usePaymentLabels();
    const queryClient = useQueryClient();
    const state = getHoaPaymentState(p, t, language);
    const directReviewCopy = getDirectHoaReviewCopy(language);
    const [showProofs, setShowProofs] = React.useState(false);
    const [manualMethod, setManualMethod] = React.useState<ManualPaymentMethod>("CASH");
    const [manualNotes, setManualNotes] = React.useState("");
    const [manualReference, setManualReference] = React.useState("");
    const [manualProof, setManualProof] = React.useState<File | null>(null);
    const [manualDate, setManualDate] = React.useState(todayInCaracas);
    const [manualUsd, setManualUsd] = React.useState("");
    const [manualVes, setManualVes] = React.useState("");
    const [reviewNotes, setReviewNotes] = React.useState("");
    
    // Fetch full invoice details to get payment proofs
    const { data: fullInvoice } = useQuery({
        queryKey: ["invoice-detail", p.id],
        queryFn: () => api.get<ApiInvoice>(`/building/payments/invoices/${p.id}/`).then((res: any) => res.data),
        enabled: !!p.id,
    });
    
    const proof = fullInvoice?.payment_proofs?.[0];
    const canRemind = p.status === "SENT" || p.status === "UNPAID";
    const canReviewDirectPayment =
        p.payment_flow === "DIRECT_HOA" && p.status === "PENDING_VERIFICATION";
    const showProofSection = !!proof && (showProofs || canReviewDirectPayment);

    const reminderMutation = useMutation({
        mutationFn: () => sendOverdueReminder(p.id),
        onSuccess: () => {
            toast.success(t("invoice.reminderSent"));
            queryClient.invalidateQueries({ queryKey: ["invoice-detail", p.id] });
        },
        onError: () => toast.error(t("invoice.reminderFailed")),
    });

    const resendMutation = useMutation({
        mutationFn: (notificationId: string) => resendNotification(notificationId),
        onSuccess: () => {
            toast.success(t("hoa.notifLog.toast.resent"));
            queryClient.invalidateQueries({ queryKey: ["invoice-detail", p.id] });
        },
        onError: () => toast.error(t("hoa.notifLog.toast.resendFailed")),
    });

    const manualMutation = useMutation({
        mutationFn: () =>
            recordManualPayment(p.id, {
                payment_method: manualMethod,
                payment_date: manualDate,
                amount_usd: manualUsd.trim() || undefined,
                amount_ves: manualVes.trim() || undefined,
                notes: manualNotes.trim() || undefined,
                reference: manualMethod === "BANK_TRANSFER" ? manualReference.trim() || undefined : undefined,
                transaction_screenshot: manualMethod === "BANK_TRANSFER" ? manualProof : null,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["hoa-admin", "payments"] });
            queryClient.invalidateQueries({ queryKey: ["hoa-admin", "incoming-payments"] });
            queryClient.invalidateQueries({ queryKey: ["invoice-detail", p.id] });
            queryClient.invalidateQueries({ queryKey: ["invoice-proofs", p.id] });
            toast.success(t("hoa.payments.toast.manualRecorded"));
            onClose();
        },
        onError: (error: any) => {
            const d = error?.response?.data;
            const msg =
                d?.detail || d?.error ||
                d?.amount_usd?.[0] || d?.amount_ves?.[0] ||
                d?.payment_date?.[0] || d?.transaction_screenshot?.[0] ||
                t("hoa.payments.toast.manualRecordFailed");
            toast.error(msg);
        },
    });

    const directReviewMutation = useMutation({
        mutationFn: ({ approve }: { approve: boolean }) =>
            verifyPayment(p.id, {
                approve,
                admin_verification_notes: reviewNotes.trim() || undefined,
            }),
        onSuccess: (_data, vars) => {
            queryClient.invalidateQueries({ queryKey: ["hoa-admin", "payments"] });
            queryClient.invalidateQueries({ queryKey: ["invoice-detail", p.id] });
            queryClient.invalidateQueries({ queryKey: ["invoice-proofs", p.id] });
            toast.success(vars.approve ? directReviewCopy.approvedToast : directReviewCopy.rejectedToast);
            onClose();
        },
        onError: (error: any) => {
            const msg =
                error?.response?.data?.detail ||
                error?.response?.data?.error ||
                t("payments.tryAgainLater");
            toast.error(msg);
        },
    });

    const canRecordManual =
        p.status !== "CANCELLED" && p.status !== "PAID" && p.payment_flow === "DIRECT_HOA";

    // The rate for the DATE BEING RECORDED, refetched whenever that date
    // changes. This form used to convert at today's live rate while
    // submitting a past payment_date, and the server stamped
    // bcv_rate_at_payment from the date -- so the stored USD and VES
    // disagreed with the stored rate. 99 of 294 proofs carrying an
    // amount_ves are inconsistent for exactly this reason, and the server
    // now rejects such a pair outright.
    const manualRateQuery = useQuery({
        queryKey: ["bcv-rate", manualDate],
        queryFn: () => getBCVRate(manualDate),
        enabled: !!manualDate,
        // A past day's rate is a fact; it will not change while the modal
        // is open, and refetching on focus would fight the user's typing.
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        retry: false,
    });

    const manualRate = manualRateQuery.data?.dollar
        ?? (p.bcv_rate_snapshot ? Number(p.bcv_rate_snapshot) : undefined);
    // No rate for that day means no honest conversion. Better an empty
    // field and a message than a number computed from the wrong day.
    const manualRateMissing = manualRateQuery.isError;
    const balanceDue = fullInvoice?.balance_due != null
        ? Number(fullInvoice.balance_due)
        : (typeof p.total_amount === "number" ? p.total_amount : Number(p.total_amount));

    // Changing the date changes the rate, which makes any amount already
    // typed inconsistent. Re-derive VES from USD rather than leaving a pair
    // the server will now reject.
    React.useEffect(() => {
        if (!manualRate || !manualUsd.trim() || Number.isNaN(Number(manualUsd))) return;
        setManualVes((Number(manualUsd) * manualRate).toFixed(2));
        // manualUsd is deliberately absent: this reacts to the RATE moving,
        // and including it would fight the user typing in the VES field.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manualRate]);

    const handleUsdChange = (v: string) => {
        setManualUsd(v);
        if (manualRate && v.trim() !== "" && !Number.isNaN(Number(v))) {
            setManualVes((Number(v) * manualRate).toFixed(2));
        } else if (v.trim() === "") {
            setManualVes("");
        }
    };
    const handleVesChange = (v: string) => {
        setManualVes(v);
        if (manualRate && v.trim() !== "" && !Number.isNaN(Number(v))) {
            setManualUsd((Number(v) / manualRate).toFixed(2));
        } else if (v.trim() === "") {
            setManualUsd("");
        }
    };

    const usdNum = Number(manualUsd);
    const manualSubmitDisabled =
        manualMutation.isPending ||
        !manualDate ||
        !manualUsd.trim() || Number.isNaN(usdNum) || usdNum <= 0 ||
        usdNum > balanceDue + 0.009 ||
        (manualMethod === "BANK_TRANSFER" && !manualProof);
    const pdfUrl = `/api/v1/building/payments/invoices/${p.id}/download/`;

    return (
        <ResponsiveModal
            open
            onClose={onClose}
            size="lg"
            headerAccent={
                isOverdue(p.status, p.due_date, p.is_overdue)
                    ? OVERDUE_TONE.rail
                    : STATUS_TONE[p.status].rail
            }
            title={t("hoa.payments.details.invoice")}
            subtitle={
                <span className="flex flex-wrap items-center gap-2">
                    <PayStatusChip status={p.status} labels={payLabels} size="sm" />
                    {isOverdue(p.status, p.due_date, p.is_overdue) && <OverdueChip />}
                    <span>{p.unitNumber}</span>
                    <span className="text-gray-300" aria-hidden>·</span>
                    <span>{fmtPeriod(p.period)}</span>
                </span>
            }
            footer={
                /* Actions live in the footer now. On a phone they were a
                   wrapping row of five buttons above the content, so the
                   invoice you opened started below the fold. */
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={() => window.open(pdfUrl, "_blank")}
                        className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-white px-3 text-sm font-medium text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
                    >
                        <FileDown className="h-4 w-4" aria-hidden />
                        <span className="hidden sm:inline">{t("invoice.download")}</span>
                    </button>
                    {canRemind && (
                        <button
                            onClick={() => reminderMutation.mutate()}
                            disabled={reminderMutation.isPending}
                            className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-amber-50 px-3 text-sm font-medium text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100 disabled:opacity-50"
                        >
                            {reminderMutation.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                            ) : (
                                <Bell className="h-4 w-4" aria-hidden />
                            )}
                            <span className="hidden sm:inline">{t("invoice.sendReminder")}</span>
                        </button>
                    )}
                    {proof && (
                        <button
                            onClick={() => setShowProofs(!showProofs)}
                            className="inline-flex h-10 items-center rounded-xl px-3 text-sm font-medium text-white"
                            style={{ background: "#1F3A93" }}
                        >
                            {showProofs ? t("common.hide") : t("hoa.payments.viewProofs")}
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="ml-auto h-10 rounded-xl bg-white px-4 text-sm font-medium text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
                    >
                        {t("common.close")}
                    </button>
                </div>
            }
        >
                    <div className="space-y-3">
                        {/* The money, first and largest.
                            It used to be the FIFTH row of twenty in one
                            undifferentiated column -- the same weight as the
                            resident's email address. What an admin opens an
                            invoice to see is how much, from whom, and whether
                            it is late. */}
                        <div className="rounded-2xl bg-white/80 p-4 ring-1 ring-black/5">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="truncate text-[15px] font-semibold text-gray-900">
                                        {p.residentName}
                                    </p>
                                    <p className="mt-0.5 truncate text-[12px] text-gray-500">
                                        {p.unitNumber} · {fmtPeriod(p.period)}
                                    </p>
                                </div>
                                <div className="shrink-0 text-right">
                                    <Money
                                        usd={p.total_amount}
                                        ves={
                                            (p.bcv_rate_snapshot ?? bcvRate)
                                                ? p.total_amount * (p.bcv_rate_snapshot ?? bcvRate)!
                                                : null
                                        }
                                        size="lg"
                                    />
                                </div>
                            </div>
                            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-black/[0.06] pt-3">
                                <WaitingLabel
                                    status={p.status}
                                    labels={payLabels}
                                    awaitingConfirmation={isAwaitingHoaConfirmation(p)}
                                />
                                <span className="text-[11px] text-gray-500">
                                    {t("hoa.payments.card.due", { date: fmtDate(p.due_date) })}
                                </span>
                            </div>
                            {state.subtitle && (
                                <p className="mt-2 text-[11px] leading-relaxed text-gray-500">
                                    {state.subtitle}
                                </p>
                            )}
                        </div>

                        <FormPanel title={t("hoa.payments.details.section.invoice")}>
                        <Row label={t("hoa.payments.details.residentEmail")} value={p.residentEmail || fullInvoice?.resident_email || "—"} />
                        {fullInvoice?.amount_paid != null && Number(fullInvoice.amount_paid) > 0 && (
                            <>
                                <Row
                                    label={t("hoa.payments.details.amountPaid")}
                                    value={`$${Number(fullInvoice.amount_paid).toLocaleString(language === "es" ? "es-VE" : "en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                />
                                <Row
                                    label={t("hoa.payments.details.balanceDue")}
                                    value={`$${Number(fullInvoice.balance_due ?? 0).toLocaleString(language === "es" ? "es-VE" : "en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                />
                            </>
                        )}
                        {p.bcv_rate_snapshot && (
                            <Row
                                label={t("hoa.payments.details.bcvRateFixed")}
                                value={
                                    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                                        <span className="tabular-nums">
                                            {p.bcv_rate_snapshot.toLocaleString(language === "es" ? "es-VE" : "en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 })} Bs./USD
                                        </span>
                                        {/* Links on the PAYMENT DATE, not on bcv_rate_date.
                                            bcv_rate_date holds the fetch time, and a rate is
                                            fetched the afternoon BEFORE the day it applies to --
                                            deriving a day from it is the exact off-by-one this
                                            whole change removed. The payment date is the day
                                            whose rate was used, so that is what we open. */}
                                        {proof?.payment_date && onViewRate && (
                                            <button
                                                type="button"
                                                onClick={() => onViewRate(proof.payment_date!)}
                                                className="text-[12px] font-medium text-[#1F3A93] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93] rounded"
                                            >
                                                {t("rates.viewInHistory")}
                                            </button>
                                        )}
                                    </span>
                                }
                            />
                        )}
                        <Row
                            label={t("hoa.payments.details.paymentMethod")}
                            value={prettyMethod(p.payment_method, t)}
                        />
                        <Row label={t("hoa.payments.details.dueDate")} value={fmtDate(p.due_date)} />
                        </FormPanel>

                        {canRecordManual && (
                            <div className="border border-[--color-border] rounded-xl p-3 bg-gray-50 space-y-3">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm font-semibold text-[--color-text]">
                                        {t("hoa.payments.manual.title")}
                                    </div>
                                    <div className="text-xs text-[--color-muted]">
                                        {t("hoa.payments.manual.remainingBalance")}:{" "}
                                        <span className="font-semibold text-[--color-text]">
                                            ${balanceDue.toLocaleString(language === "es" ? "es-VE" : "en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                    <div className="space-y-1">
                                        <label className="text-xs font-medium text-[--color-muted]">
                                            {t("hoa.payments.manual.method")}
                                        </label>
                                        <select
                                            value={manualMethod}
                                            onChange={(e) => setManualMethod(e.target.value as ManualPaymentMethod)}
                                            className="w-full rounded-lg border border-[--color-border] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--color-primary]"
                                        >
                                            <option value="CASH">{t("hoa.payments.form.paymentMethod.cash")}</option>
                                            <option value="BANK_TRANSFER">{t("hoa.payments.form.paymentMethod.bank")}</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-medium text-[--color-muted]">
                                            {t("hoa.payments.manual.paymentDate")}
                                        </label>
                                        <input
                                            type="date"
                                            value={manualDate}
                                            max={todayInCaracas()}
                                            onChange={(e) => setManualDate(e.target.value)}
                                            className="w-full rounded-lg border border-[--color-border] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--color-primary]"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                    <div className="space-y-1">
                                        <label className="text-xs font-medium text-[--color-muted]">
                                            {t("hoa.payments.manual.amountUsd")}
                                        </label>
                                        <input
                                            type="number"
                                            inputMode="decimal"
                                            min="0"
                                            step="0.01"
                                            value={manualUsd}
                                            onChange={(e) => handleUsdChange(e.target.value)}
                                            placeholder="0.00"
                                            className="w-full rounded-lg border border-[--color-border] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--color-primary]"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-medium text-[--color-muted]">
                                            {t("hoa.payments.manual.amountVes")}
                                        </label>
                                        <input
                                            type="number"
                                            inputMode="decimal"
                                            min="0"
                                            step="0.01"
                                            value={manualVes}
                                            onChange={(e) => handleVesChange(e.target.value)}
                                            placeholder="0.00"
                                            className="w-full rounded-lg border border-[--color-border] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--color-primary]"
                                        />
                                    </div>
                                </div>
                                {manualRateMissing ? (
                                    <div className="text-[11px] text-red-700">
                                        {t("hoa.payments.manual.rateMissing")}
                                    </div>
                                ) : manualRate ? (
                                    <div className="text-[11px] text-[--color-muted]">
                                        {t("hoa.payments.manual.rateHint")} {manualRate.toLocaleString(language === "es" ? "es-VE" : "en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 })} Bs./USD
                                        {" · "}
                                        {t("hoa.payments.manual.rateForDate", { date: manualRateQuery.data?.date ?? manualDate })}
                                        {manualRateQuery.data && manualRateQuery.data.is_exact === false && (
                                            <span> · {t("hoa.payments.manual.rateCarried", { days: String(manualRateQuery.data.days_carried ?? 0) })}</span>
                                        )}
                                    </div>
                                ) : null}

                                {manualMethod === "BANK_TRANSFER" && (
                                    <>
                                        <div className="space-y-1">
                                            <label className="text-xs font-medium text-[--color-muted]">
                                                {t("hoa.payments.manual.proofRequired")}
                                            </label>
                                            <input
                                                type="file"
                                                accept="image/*,application/pdf"
                                                onChange={(e) => setManualProof(e.target.files?.[0] ?? null)}
                                                className="w-full rounded-lg border border-[--color-border] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--color-primary] file:mr-3 file:rounded-md file:border-0 file:bg-gray-100 file:px-3 file:py-1 file:text-xs"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-medium text-[--color-muted]">
                                                {t("hoa.payments.manual.referenceOptional")}
                                            </label>
                                            <input
                                                type="text"
                                                value={manualReference}
                                                onChange={(e) => setManualReference(e.target.value)}
                                                className="w-full rounded-lg border border-[--color-border] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--color-primary]"
                                                placeholder={t("hoa.payments.manual.referencePlaceholder")}
                                            />
                                        </div>
                                    </>
                                )}

                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-[--color-muted]">
                                        {t("hoa.payments.manual.notesOptional")}
                                    </label>
                                    <textarea
                                        value={manualNotes}
                                        onChange={(e) => setManualNotes(e.target.value)}
                                        className="w-full rounded-lg border border-[--color-border] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--color-primary]"
                                        rows={2}
                                        placeholder={t("hoa.payments.manual.notesPlaceholder")}
                                    />
                                </div>

                                <button
                                    onClick={() => manualMutation.mutate()}
                                    disabled={manualSubmitDisabled}
                                    className="w-full rounded-lg bg-emerald-600 text-white px-3 py-2 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
                                >
                                    {manualMutation.isPending
                                        ? t("hoa.payments.manual.recording")
                                        : t("hoa.payments.manual.submit")}
                                </button>
                            </div>
                        )}

                        {/* NEW: timestamps for the flow */}
                        <Row
                            label={t("hoa.payments.details.markedPaidAt")}
                            value={
                                p.marked_paid_at
                                    ? fmtDateTime(p.marked_paid_at)
                                    : "—"
                            }
                        />
                        <Row
                            label={t("hoa.payments.details.verifiedAt")}
                            value={
                                p.verified_at ? fmtDateTime(p.verified_at) : "—"
                            }
                        />
                        <Row
                            label={t("hoa.payments.details.finalizedAt")}
                            value={
                                p.finalized_at
                                    ? fmtDateTime(p.finalized_at)
                                    : "—"
                            }
                        />
                        <Row
                            label={t("hoa.payments.details.createdAt")}
                            value={fmtDateTime(p.created_at)}
                        />
                        <Row
                            label={t("hoa.payments.details.updatedAt")}
                            value={fmtDateTime(p.updated_at)}
                        />

                        <Row label={t("hoa.payments.details.notes")} value={p.notes ?? "—"} />

                        {canReviewDirectPayment && (
                            <div className="pt-3 border-t border-gray-200 space-y-3">
                                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                                    <h4 className="text-sm font-semibold text-amber-900">
                                        {directReviewCopy.reviewTitle}
                                    </h4>
                                    <p className="mt-1 text-sm text-amber-800">
                                        {directReviewCopy.reviewSubtitle}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-[--color-muted]">
                                        {directReviewCopy.notesLabel}
                                    </label>
                                    <textarea
                                        value={reviewNotes}
                                        onChange={(e) => setReviewNotes(e.target.value)}
                                        className="mt-1 w-full rounded-lg border border-[--color-border] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[--color-primary]"
                                        rows={3}
                                        placeholder={directReviewCopy.notesPlaceholder}
                                    />
                                </div>
                                <div className="flex flex-col gap-2 sm:flex-row">
                                    <button
                                        type="button"
                                        onClick={() => directReviewMutation.mutate({ approve: true })}
                                        disabled={directReviewMutation.isPending}
                                        className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                                    >
                                        {directReviewMutation.isPending ? "…" : directReviewCopy.approveLabel}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => directReviewMutation.mutate({ approve: false })}
                                        disabled={directReviewMutation.isPending}
                                        className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
                                    >
                                        {directReviewMutation.isPending ? "…" : directReviewCopy.rejectLabel}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Notification log */}
                        {fullInvoice?.notifications && fullInvoice.notifications.length > 0 && (
                            <div className="pt-3 border-t border-gray-200">
                                <label className="text-xs font-medium text-[--color-muted] block mb-2">
                                    {t("invoice.notifications")}
                                </label>
                                <div className="space-y-1.5">
                                    {fullInvoice.notifications.slice(0, 3).map((n: InvoiceNotification) => (
                                        <div key={n.id} className="flex items-center gap-2 text-xs">
                                            <span className={`inline-block rounded px-1.5 py-0.5 font-semibold ${
                                                n.status === "SENT" ? "bg-emerald-100 text-emerald-700" :
                                                n.status === "FAILED" ? "bg-red-100 text-red-700" :
                                                "bg-gray-100 text-gray-500"
                                            }`}>
                                                {n.channel === "EMAIL" ? (
                                                    <Mail className="inline h-3.5 w-3.5 align-[-2px]" aria-hidden />
                                                ) : (
                                                    <Smartphone className="inline h-3.5 w-3.5 align-[-2px]" aria-hidden />
                                                )}{" "}
                                                {n.status}
                                            </span>
                                            <span className="text-gray-400 truncate">
                                                {n.notification_type === "OVERDUE_REMINDER" ? t("invoice.sendReminder") : t("invoice.notifSent")}
                                                {n.channel === "SMS" && n.recipient_phone ? ` → ${n.recipient_phone}` : ""}
                                                {n.channel === "EMAIL" && n.recipient_email ? ` → ${n.recipient_email}` : ""}
                                            </span>
                                            {n.status === "FAILED" && (
                                                <button
                                                    onClick={() => resendMutation.mutate(n.id)}
                                                    disabled={resendMutation.isPending}
                                                    className="ml-1 rounded px-1.5 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 border border-blue-200"
                                                    title={t("hoa.notifLog.retryNotification")}
                                                >
                                                    <RotateCw className="mr-1 inline h-3 w-3 align-[-1px]" aria-hidden />
                                                    {t("hoa.notifLog.resend")}
                                                </button>
                                            )}
                                            <span className="text-gray-300 ml-auto whitespace-nowrap">
                                                {fmtDate(n.sent_at)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {canReviewDirectPayment && !proof && (
                            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                                {directReviewCopy.noProofWarning}
                            </div>
                        )}

                        {/* Resident proof or forwarding proof details */}
                        {showProofSection && proof && (
                            <div className="pt-3 border-t border-gray-200 space-y-4">
                                <div>
                                    <label className="text-xs font-medium text-[--color-muted] block mb-2">
                                        {directReviewCopy.proofTitle}
                                    </label>
                                    <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 border">
                                        <img
                                            src={mediaUrl(proof.transaction_screenshot) ?? undefined}
                                            alt="Resident payment proof"
                                            className="w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                            onClick={() => openMedia(proof.transaction_screenshot)}
                                        />
                                    </div>
                                    <button
                                        onClick={() => openMedia(proof.transaction_screenshot)}
                                        className="w-full text-center text-sm text-indigo-600 hover:text-indigo-800 font-medium mt-2"
                                    >
                                        {t("hoa.payments.openInNewTab")}
                                        <ExternalLink className="ml-1 inline h-3 w-3 align-[-1px]" aria-hidden />
                                    </button>
                                </div>

                                <div>
                                    <label className="text-xs font-medium text-[--color-muted] block mb-2">
                                        {directReviewCopy.idTitle}
                                    </label>
                                    <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 border">
                                        <img
                                            src={mediaUrl(proof.id_verification) ?? undefined}
                                            alt="Resident ID verification"
                                            className="w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                            onClick={() => openMedia(proof.id_verification)}
                                        />
                                    </div>
                                    <button
                                        onClick={() => openMedia(proof.id_verification)}
                                        className="w-full text-center text-sm text-indigo-600 hover:text-indigo-800 font-medium mt-2"
                                    >
                                        {t("hoa.payments.openInNewTab")}
                                        <ExternalLink className="ml-1 inline h-3 w-3 align-[-1px]" aria-hidden />
                                    </button>
                                </div>

                                {proof.resident_notes && (
                                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                            {directReviewCopy.residentNotesTitle}
                                        </div>
                                        <div className="mt-1 text-sm text-slate-700">
                                            {proof.resident_notes}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Forwarding Information from Super Admin - legacy flow only */}
                        {showProofs && proof && p.payment_flow === "PLATFORM_RELAY" && (
                            <div className="pt-3 border-t border-gray-200 space-y-3">
                                {proof.forwarding_payment_method && (
                                    <Row 
                                        label={t("hoa.payments.proofs.forwardingMethod")} 
                                        value={
                                            proof.forwarding_payment_method === "BANK_TRANSFER" ? t("hoa.payments.form.paymentMethod.bank") :
                                            proof.forwarding_payment_method === "ZELLE" ? t("hoa.payments.form.paymentMethod.zelle") :
                                            proof.forwarding_payment_method === "STRIPE" ? t("hoa.payments.proofs.stripePaypal") : "—"
                                        } 
                                    />
                                )}
                                
                                {proof.forwarding_notes && (
                                    <Row label={t("hoa.payments.proofs.forwardingNotes")} value={proof.forwarding_notes} />
                                )}
                                
                                {/* Forwarding Proof from Super Admin */}
                                {proof.forwarding_proof && (
                                    <div className="pt-2">
                                        <label className="text-xs font-medium text-[--color-muted] block mb-2">
                                            {t("hoa.payments.proofs.transferProofFromUrbe")}
                                        </label>
                                        <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 border">
                                            <img
                                                src={mediaUrl(proof.forwarding_proof) ?? undefined}
                                                alt="Transfer proof"
                                                className="w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                                onClick={() => openMedia(proof.forwarding_proof)}
                                            />
                                        </div>
                                        <button
                                            onClick={() => openMedia(proof.forwarding_proof)}
                                            className="w-full text-center text-sm text-indigo-600 hover:text-indigo-800 font-medium mt-2"
                                        >
                                            {t("hoa.payments.openInNewTab")}
                                        <ExternalLink className="ml-1 inline h-3 w-3 align-[-1px]" aria-hidden />
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
        </ResponsiveModal>
    );
}

/** Payment Proofs Modal - shows forwarding proof from Super Admin */
function PaymentProofsModal({ payment, onClose }: { payment: Payment; onClose: () => void }) {
    const { t } = useLanguage();
    // Fetch full invoice details to get payment proofs
    const { data: fullInvoice, isLoading } = useQuery({
        queryKey: ["invoice-proofs", payment.id],
        queryFn: () => api.get<ApiInvoice>(`/building/payments/invoices/${payment.id}/`).then((res: any) => res.data),
        enabled: !!payment.id,
    });
    
    const proof = fullInvoice?.payment_proofs?.[0];

    return (
        <ResponsiveModal
            open
            onClose={onClose}
            headerAccent={STATUS_TONE[payment.status].rail}
            title={t("hoa.payments.modal.proofsTitle")}
            subtitle={
                <span className="flex flex-wrap items-center gap-2">
                    <span>{t("hoa.payments.card.unit", { unit: payment.unitNumber })}</span>
                    <span className="text-gray-300" aria-hidden>·</span>
                    <span>{payment.residentName}</span>
                    <span className="text-gray-300" aria-hidden>·</span>
                    <span>{fmtPeriod(payment.period)}</span>
                </span>
            }
        >
                <div>

                    {isLoading ? (
                        <div className="py-8 text-center text-gray-500">{t("hoa.payments.proofs.loading")}</div>
                    ) : !proof ? (
                        <div className="py-8 text-center text-gray-500">
                            <ImageIcon className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                            <p>{t("hoa.payments.proofs.noProofs")}</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Forwarding Method */}
                            {proof.forwarding_payment_method && (
                                <div className="flex items-center justify-between p-3 rounded-lg bg-indigo-50">
                                    <span className="text-sm text-gray-600">{t("hoa.payments.proofs.transferMethod")}</span>
                                    <span className="text-sm font-medium text-indigo-700">
                                        {proof.forwarding_payment_method === "BANK_TRANSFER" ? t("hoa.payments.form.paymentMethod.bank") :
                                         proof.forwarding_payment_method === "ZELLE" ? t("hoa.payments.form.paymentMethod.zelle") :
                                         proof.forwarding_payment_method === "STRIPE" ? t("hoa.payments.proofs.stripePaypal") : proof.forwarding_payment_method}
                                    </span>
                                </div>
                            )}

                            {/* Forwarding Notes */}
                            {proof.forwarding_notes && (
                                <div className="p-3 rounded-lg bg-gray-50">
                                    <div className="text-xs font-medium text-gray-500 mb-1">{t("hoa.payments.proofs.notesFromUrbe")}</div>
                                    <p className="text-sm text-gray-700">{proof.forwarding_notes}</p>
                                </div>
                            )}

                            {/* Forwarding Proof Image */}
                            {proof.forwarding_proof && (
                                <div>
                                    <div className="text-xs font-medium text-gray-500 mb-2">{t("hoa.payments.proofs.transferProof")}</div>
                                    <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 border cursor-pointer"
                                         onClick={() => openMedia(proof.forwarding_proof)}>
                                        <img
                                            src={mediaUrl(proof.forwarding_proof) ?? undefined}
                                            alt="Transfer proof"
                                            className="w-full h-full object-contain hover:opacity-90 transition-opacity"
                                        />
                                    </div>
                                    <button
                                        onClick={() => openMedia(proof.forwarding_proof)}
                                        className="w-full text-center text-sm text-indigo-600 hover:text-indigo-800 font-medium mt-2"
                                    >
                                        {t("hoa.payments.openInNewTab")}
                                        <ExternalLink className="ml-1 inline h-3 w-3 align-[-1px]" aria-hidden />
                                    </button>
                                </div>
                            )}

                            {/* Show message if no forwarding info */}
                            {!proof.forwarding_payment_method && !proof.forwarding_notes && !proof.forwarding_proof && (
                                <div className="py-4 text-center text-gray-500">
                                    <p className="text-sm">{t("hoa.payments.proofs.noTransferInfo")}</p>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="mt-5 flex justify-end">
                        <button
                            onClick={onClose}
                            className="rounded-xl bg-white px-4 py-2 text-sm text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
                        >{t("common.close")}</button>
                    </div>
                </div>
        </ResponsiveModal>
    );
}

function ConfirmModal({
    tone,
    title,
    message,
    confirmLabel,
    onCancel,
    onConfirm,
    confirming,
}: {
    tone: "danger" | "warning";
    title: string;
    message: string;
    confirmLabel: string;
    onCancel: () => void;
    onConfirm: () => void;
    confirming: boolean;
}) {
    const { t } = useLanguage();
    const palette =
        tone === "danger"
            ? { bg: "bg-red-50", text: "text-red-600", btn: "#DC2626" }
            : { bg: "bg-amber-50", text: "text-amber-700", btn: "#D97706" };

    return (
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-[2px]" onClick={onCancel} />
            <div className="absolute inset-x-0 bottom-0 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:w-full sm:max-w-md sm:-translate-x-1/2 sm:-translate-y-1/2">
                <div className="m-0 rounded-t-3xl bg-white p-6 shadow-2xl ring-1 ring-black/5 sm:rounded-3xl">
                    <div className="mb-3 flex items-start gap-3">
                        <div
                            className={`grid h-10 w-10 place-items-center rounded-xl ${palette.bg} ${palette.text}`}
                        >
                            <WarningIcon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-[16px] font-semibold text-gray-900">
                                {title}
                            </h3>
                            <p className="mt-1 text-sm text-gray-600">
                                {message}
                            </p>
                        </div>
                    </div>
                    <div className="mt-5 flex items-center justify-end gap-2">
                        <button
                            type="button"
                            className="rounded-2xl border border-[--color-border] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            onClick={onCancel}
                        >{t("common.cancel")}</button>
                        <button
                            type="button"
                            disabled={confirming}
                            className="rounded-2xl px-4 py-2 text-sm font-semibold text-white"
                            style={{ background: palette.btn }}
                            onClick={onConfirm}
                        >
                            {confirming ? t("common.working") : confirmLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/** =========================
 * Small bits & helpers
 * ========================= */
function Toast({
    children,
    onClose,
}: {
    children: React.ReactNode;
    onClose: () => void;
}) {
    React.useEffect(() => {
        const t = setTimeout(onClose, 6000);
        return () => clearTimeout(t);
    }, [onClose]);
    return (
        <div className="fixed bottom-4 right-4 z-50 max-w-sm rounded-2xl border border-[--color-border] bg-white p-4 shadow-lg">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">{children}</div>
                <button className="text-[--color-muted]" onClick={onClose}>
                    <X className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex items-start justify-between gap-4">
            <span className="text-xs text-[--color-muted]">{label}</span>
            <span className="text-sm text-[color:var(--color-fg)]">
                {value}
            </span>
        </div>
    );
}


function TrendingUpIcon({ className = "" }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M3 17l6-6 4 4 7-7M14 8h7v7" stroke="currentColor" strokeWidth="1.8"
                  strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function InvoiceIcon({ className = "" }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
                d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <polyline
                points="14 2 14 8 20 8"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="1.8" />
            <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="1.8" />
            <polyline points="10 9 9 9 8 9" stroke="currentColor" strokeWidth="1.8" />
        </svg>
    );
}

function ImageIcon({ className = "" }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
            <rect
                x="3"
                y="3"
                width="18"
                height="18"
                rx="2"
                stroke="currentColor"
                strokeWidth="1.8"
            />
            <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
            <path
                d="M21 15l-5-5L5 21"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function WarningIcon({ className = "" }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
                d="M12 8v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
            />
        </svg>
    );
}
function SearchIcon({ className = "" }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
            <circle
                cx="11"
                cy="11"
                r="7"
                stroke="currentColor"
                strokeWidth="1.6"
            />
            <path
                d="M20 20l-3.5-3.5"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
            />
        </svg>
    );
}
function ExchangeIcon({ className = "" }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
            <path
                d="M7 10l-3 3 3 3"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M4 13h13"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
            />
            <path
                d="M17 14l3-3-3-3"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M20 11H7"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
            />
        </svg>
    );
}

function RefreshIcon({ className = "" }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
            <path
                d="M4 12a8 8 0 0 1 14.93-4M20 12a8 8 0 0 1-14.93 4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
            />
            <path
                d="M4 4v4h4M20 20v-4h-4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

/** =========================
 * Incoming Payments Section
 * ========================= */
function IncomingPaymentDetailsModal({
    invoice,
    onClose,
    onConfirm,
    isConfirming,
    bcvRate,
}: {
    invoice: ApiInvoice;
    onClose: () => void;
    onConfirm: (id: string) => void;
    isConfirming: boolean;
    bcvRate?: number;
}) {
    const { t, language } = useLanguage();
    const hasProofs = invoice.payment_proofs && invoice.payment_proofs.length > 0;
    const proof = hasProofs ? invoice.payment_proofs![0] : null;

    // Fetch payment details (platform and HOA)
    const { data: paymentDetails } = useQuery<InvoicePaymentDetails>({
        queryKey: ["invoice-payment-details", invoice.id],
        queryFn: () => getInvoicePaymentDetails(invoice.id),
        enabled: !!invoice.id,
    });

    return (
        <ResponsiveModal
            open
            onClose={onClose}
            title={t("hoa.payments.incoming.detailsTitle")}
            subtitle={
                <span className="flex flex-wrap items-center gap-2">
                    <span>{invoice.unit_code || "—"}</span>
                    <span className="text-gray-300" aria-hidden>·</span>
                    <span>{invoice.resident_name}</span>
                </span>
            }
        >
            <div>

                {/* Payment Info */}
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-medium text-[--color-muted]">{t("hoa.payments.table.unit")}</label>
                            <p className="text-sm font-semibold">{invoice.unit_code || "—"}</p>
                        </div>
                        <div>
                            <label className="text-xs font-medium text-[--color-muted]">{t("hoa.payments.table.resident")}</label>
                            <p className="text-sm font-semibold">{invoice.resident_name || invoice.resident_email}</p>
                        </div>
                        <div>
                            <label className="text-xs font-medium text-[--color-muted]">{t("hoa.payments.table.period")}</label>
                            <p className="text-sm font-semibold">{fmtPeriod(invoice.period)}</p>
                        </div>
                        <div>
                            <label className="text-xs font-medium text-[--color-muted]">{t("hoa.payments.table.amount")}</label>
                            <DualCurrency usd={parseFloat(invoice.total_amount)} bcvRate={invoice.bcv_rate_snapshot ? parseFloat(invoice.bcv_rate_snapshot) : bcvRate} bcvDate={invoice.bcv_rate_date} />
                            {invoice.bcv_rate_snapshot && (
                                <p className="text-[10px] text-[--color-muted] mt-0.5">
                                    {t("hoa.payments.details.bcvRateFixedLine", {
                                        rate: parseFloat(invoice.bcv_rate_snapshot).toLocaleString(language === "es" ? "es-VE" : "en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 }),
                                    })}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Commission Breakdown */}
                    <CommissionBreakdown invoice={invoice} />

                    {/* Payment Details - HOA's account where payment will arrive */}
                    {paymentDetails?.hoa_payment_details && (
                        <PaymentDetailsCard
                            details={paymentDetails.hoa_payment_details}
                            title={t("hoa.payments.incoming.yourPaymentAccount")}
                            description={t("hoa.payments.incoming.fundsTransferredHere")}
                            showVerificationBadge={true}
                        />
                    )}

                    {/* Original Payment Method from Resident */}
                    {invoice.payment_method && (
                        <div className="rounded-xl border border-[--color-border] bg-gray-50 p-4">
                            <h3 className="text-sm font-medium mb-3 text-gray-900">{t("hoa.payments.incoming.residentPaymentMethod")}</h3>
                            <p className="text-sm font-semibold text-gray-700">
                                {prettyMethod(invoice.payment_method, t)}
                            </p>
                        </div>
                    )}

                    {/* Forwarding Info */}
                    <div className="rounded-xl bg-blue-50 p-4">
                        <h3 className="text-sm font-medium text-blue-900 mb-2">{t("hoa.payments.incoming.forwardingInfo")}</h3>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                                <span className="text-blue-700">{t("hoa.payments.incoming.forwardedByLabel")}</span>
                                <span className="ml-2 font-medium text-blue-900">{invoice.forwarded_by_name || "—"}</span>
                            </div>
                            <div>
                                <span className="text-blue-700">{t("common.date")}:</span>
                                <span className="ml-2 font-medium text-blue-900">
                                    {invoice.forwarded_at ? fmtDateTime(invoice.forwarded_at) : "—"}
                                </span>
                            </div>
                        </div>
                        {/* Super Admin's Payment Method */}
                        {proof?.forwarding_payment_method && (
                            <div className="mt-3 pt-3 border-t border-blue-200">
                                <span className="text-blue-700 text-sm">{t("hoa.payments.incoming.paymentMethodUsed")}</span>
                                <span className="ml-2 font-semibold text-blue-900">
                                    {proof.forwarding_payment_method === "BANK_TRANSFER" && t("hoa.payments.form.paymentMethod.bank")}
                                    {proof.forwarding_payment_method === "ZELLE" && t("hoa.payments.form.paymentMethod.zelle")}
                                    {proof.forwarding_payment_method === "STRIPE" && t("hoa.payments.proofs.stripePaypal")}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Forwarding Proof from Super Admin */}
                    {proof?.forwarding_proof && (
                        <div className="rounded-xl border border-[--color-border] p-4">
                            <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                                <ImageIcon className="h-4 w-4" />
                                {t("hoa.payments.proofs.transferProofFromUrbe")}
                            </h3>
                            <div className="space-y-3">
                                <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 border">
                                    <img
                                        src={mediaUrl(proof.forwarding_proof) ?? undefined}
                                        alt="Transfer proof"
                                        className="w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() => openMedia(proof.forwarding_proof)}
                                    />
                                </div>
                                <button
                                    onClick={() => openMedia(proof.forwarding_proof)}
                                    className="w-full text-center text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                                >
                                    {t("hoa.payments.openInNewTab")}
                                        <ExternalLink className="ml-1 inline h-3 w-3 align-[-1px]" aria-hidden />
                                </button>
                            </div>
                            {proof.forwarding_notes && (
                                <div className="mt-3 text-sm">
                                    <span className="font-medium text-gray-700">{t("hoa.payments.details.notes")}:</span>
                                    <p className="mt-1 text-gray-600">{proof.forwarding_notes}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {!proof?.forwarding_proof && (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                            <p className="text-sm text-amber-800">
                                {t("hoa.payments.proofs.noTransferProof")}
                            </p>
                        </div>
                    )}

                    {/* Original Resident Payment Proof */}
                    <div className="space-y-4">
                        {proof?.transaction_screenshot && (
                            <div className="rounded-xl border border-[--color-border] p-4">
                                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                                    <ImageIcon className="h-4 w-4" />
                                    {t("hoa.payments.proofs.residentTransactionProof")}
                                </h3>
                                <div className="space-y-3">
                                    <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 border">
                                        <img
                                            src={mediaUrl(proof.transaction_screenshot) ?? undefined}
                                            alt="Resident payment proof"
                                            className="w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                            onClick={() => openMedia(proof.transaction_screenshot)}
                                        />
                                    </div>
                                    <button
                                        onClick={() => openMedia(proof.transaction_screenshot)}
                                        className="w-full text-center text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                                    >
                                        {t("hoa.payments.openInNewTab")}
                                        <ExternalLink className="ml-1 inline h-3 w-3 align-[-1px]" aria-hidden />
                                    </button>
                                </div>
                                {proof.resident_notes && (
                                    <div className="mt-3 text-sm">
                                        <span className="font-medium text-gray-700">{t("hoa.payments.proofs.residentNotes")}:</span>
                                        <p className="mt-1 text-gray-600">{proof.resident_notes}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {proof?.id_verification && (
                            <div className="rounded-xl border border-[--color-border] p-4">
                                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                                    <ImageIcon className="h-4 w-4" />
                                    {t("hoa.payments.proofs.residentIdVerification")}
                                </h3>
                                <div className="space-y-3">
                                    <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 border">
                                        <img
                                            src={mediaUrl(proof.id_verification) ?? undefined}
                                            alt="ID verification"
                                            className="w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                            onClick={() => openMedia(proof.id_verification)}
                                        />
                                    </div>
                                    <button
                                        onClick={() => openMedia(proof.id_verification)}
                                        className="w-full text-center text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                                    >
                                        {t("hoa.payments.openInNewTab")}
                                        <ExternalLink className="ml-1 inline h-3 w-3 align-[-1px]" aria-hidden />
                                    </button>
                                </div>
                            </div>
                        )}

                        {!proof?.transaction_screenshot && !proof?.id_verification && (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                                <p className="text-sm text-amber-800">
                                    {t("hoa.payments.proofs.noResidentProofs")}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Action buttons */}
                <div className="mt-6 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="rounded-xl border border-[--color-border] bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >{t("common.close")}</button>
                    <button
                        onClick={() => onConfirm(invoice.id)}
                        disabled={isConfirming}
                        className="rounded-xl bg-emerald-600 text-white px-4 py-2 text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                    >
                        {isConfirming ? t("hoa.payments.modal.confirming") : t("hoa.payments.confirmReceipt")}
                    </button>
                </div>
            </div>
        </ResponsiveModal>
    );
}

function fmtMoney(n: number) {
    const locale = getAppLocale();
    try {
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: "USD",
        }).format(n);
    } catch {
        return `$${n.toFixed(2)}`;
    }
}

function fmtMoneyVes(n: number) {
    const locale = getAppLocale();
    try {
        return new Intl.NumberFormat(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(n) + ' Bs.';
    } catch {
        return `${n.toFixed(2)} Bs.`;
    }
}

/** Component to display dual currency (USD + VES) */
function DualCurrency({ usd, bcvRate, bcvDate, compact = false }: { usd: number; bcvRate?: number; bcvDate?: string; compact?: boolean }) {
    const vesAmount = bcvRate ? usd * bcvRate : null;
    
    if (compact) {
        return (
            <div className="text-sm">
                <span className="font-medium">{fmtMoney(usd)}</span>
                {vesAmount !== null && (
                    <span className="text-gray-400 text-xs ml-1">
                        ({fmtMoneyVes(vesAmount)})
                    </span>
                )}
            </div>
        );
    }
    
    return (
        <div>
            <div className="font-medium">{fmtMoney(usd)}</div>
            {vesAmount !== null && (
                <div className="text-xs text-gray-400">{fmtMoneyVes(vesAmount)}</div>
            )}
        </div>
    );
}

function fmtPeriod(yyyymm: string) {
    const locale = getAppLocale();
    const [y, m] = yyyymm.split("-").map(Number);
    return new Date(y, (m || 1) - 1, 1).toLocaleDateString(locale, {
        month: "short",
        year: "numeric",
    });
}
function fmtDate(iso: string) {
    const locale = getAppLocale();
    try {
        return new Date(iso).toLocaleDateString(locale, { timeZone: 'America/Caracas' });
    } catch {
        return new Date(iso).toLocaleDateString(locale);
    }
}
function fmtDateTime(iso: string) {
    const locale = getAppLocale();
    try {
        return new Date(iso).toLocaleString(locale, { timeZone: 'America/Caracas' });
    } catch {
        return new Date(iso).toLocaleString(locale);
    }
}
function toDateInputValue(iso: string) {
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}
function prettyStatus(s: InvoiceStatus, t: (key: string) => string) {
    switch (s) {
        case "DRAFT":
            return t("hoa.payments.status.draft");
        case "SENT":
            return t("hoa.payments.status.sent");
        case "UNPAID":
            return t("hoa.payments.status.unpaid");
        case "PENDING_VERIFICATION":
            return t("hoa.payments.status.pendingVerification");
        case "PENDING_HOA_TRANSFER":
            return t("hoa.payments.status.pendingHoaTransfer");
        case "PAID":
            return t("hoa.payments.status.paid");
        case "CANCELLED":
            return t("hoa.payments.status.cancelled");
    }
}
function prettyMethod(m: PaymentMethod | undefined, t: (key: string) => string) {
    if (!m) return "—";
    if (m === "BANK_TRANSFER") return t("hoa.payments.form.paymentMethod.bank");
    if (m === "STRIPE") return t("hoa.payments.form.paymentMethod.stripe");
    if (m === "PAGO_MOVIL") return t("hoa.payments.form.paymentMethod.pagoMovil");
    if (m === "CASH") return t("hoa.payments.form.paymentMethod.cash");
    if (m === "ZELLE") return t("hoa.payments.form.paymentMethod.zelle");
    return m;
}

function getAppLocale() {
    if (typeof document !== "undefined") {
        const langAttr = document.documentElement.lang || "";
        if (langAttr.toLowerCase().startsWith("es")) return "es-VE";
        if (langAttr.toLowerCase().startsWith("en")) return "en-US";
    }
    if (typeof window !== "undefined") {
        const stored = window.localStorage.getItem("app_language");
        if (stored === "es") return "es-VE";
        if (stored === "en") return "en-US";
    }
    return "en-US";
}

/* ================================================================== *
 * Overdue reminders — pick who, then send
 *
 * The button was "Send All Overdue Reminders" and it meant it: one click
 * mailed every eligible resident with no list, no preview and no undo. The
 * admin who had spoken to someone that morning, or who knew a transfer was
 * already in flight, had no way to leave them out.
 *
 * The list comes from the server rather than from the rows on screen. The
 * eligibility rule is six conditions deep and three of them are about
 * payment activity rather than status, so reproducing it here would drift
 * and the dialog would start lying about what the button does.
 * ================================================================== */
function OverdueReminderModal({
    onClose,
    onSent,
}: {
    onClose: () => void;
    onSent: (result: BulkReminderResult) => void;
}) {
    const { t } = useLanguage();

    const candidates = useQuery({
        queryKey: ["hoa-admin", "overdue-reminder-candidates"],
        queryFn: getOverdueReminderCandidates,
        // Always fresh: this list decides who receives mail, and a cached
        // copy could name someone who paid an hour ago.
        staleTime: 0,
        refetchOnMount: "always",
    });

    const rows = React.useMemo(() => candidates.data ?? [], [candidates.data]);
    const [selected, setSelected] = React.useState<Set<string> | null>(null);

    // Everyone ticked on arrival, so the common case stays one extra click
    // and you untick the person you spoke to. `null` until the list lands,
    // so an empty set is never mistaken for "the admin deselected all".
    React.useEffect(() => {
        if (selected === null && rows.length) {
            setSelected(new Set(rows.map((r) => r.invoice_id)));
        }
    }, [rows, selected]);

    const chosen = selected ?? new Set<string>();
    const allSelected = rows.length > 0 && chosen.size === rows.length;

    function toggle(id: string) {
        setSelected((current) => {
            const next = new Set(current ?? []);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    const sendMutation = useMutation({
        mutationFn: () => bulkSendReminders([...chosen]),
        onSuccess: onSent,
        onError: () => toast.error(t("hoa.payments.bulkReminderFailedToast")),
    });

    const busy = sendMutation.isPending;

    return (
        <ResponsiveModal
            open
            onClose={onClose}
            title={t("hoa.payments.reminderModal.title")}
            subtitle={t("hoa.payments.reminderModal.subtitle")}
            headerAccent={OVERDUE_TONE.rail}
            size="lg"
            footer={
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="h-11 flex-1 rounded-xl bg-white text-sm font-medium text-gray-700 ring-1 ring-gray-200 transition hover:bg-gray-50 disabled:opacity-50"
                    >
                        {t("common.cancel")}
                    </button>
                    <button
                        type="button"
                        onClick={() => sendMutation.mutate()}
                        // Zero selected is a real state, and sending to nobody
                        // is not something to allow by accident.
                        disabled={busy || chosen.size === 0}
                        className="inline-flex h-11 flex-[2] items-center justify-center gap-2 rounded-xl text-sm font-semibold text-white shadow-sm transition active:scale-[0.99] disabled:opacity-50"
                        style={{ background: OVERDUE_TONE.rail }}
                    >
                        {busy ? (
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                        ) : (
                            <Bell className="h-4 w-4" aria-hidden />
                        )}
                        {busy
                            ? t("hoa.payments.bulkReminderSending")
                            : t("hoa.payments.reminderModal.send", { count: chosen.size })}
                    </button>
                </div>
            }
        >
            {candidates.isLoading ? (
                <PaymentListSkeleton rows={4} />
            ) : candidates.isError ? (
                <p className="rounded-xl bg-red-50 px-3.5 py-2.5 text-sm text-red-800 ring-1 ring-red-200">
                    {t("hoa.payments.reminderModal.loadFailed")}
                </p>
            ) : rows.length === 0 ? (
                <PaymentEmptyState
                    title={t("hoa.payments.reminderModal.noneTitle")}
                    hint={t("hoa.payments.reminderModal.noneHint")}
                />
            ) : (
                <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3 rounded-xl bg-gray-50 px-3 py-2.5 ring-1 ring-gray-200">
                        <label className="flex cursor-pointer items-center gap-2.5 text-[13px] font-medium text-gray-800">
                            <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={() =>
                                    setSelected(
                                        allSelected
                                            ? new Set()
                                            : new Set(rows.map((r) => r.invoice_id))
                                    )
                                }
                                className="h-4 w-4 rounded border-gray-300 text-[#1F3A93] focus:ring-[#1F3A93]"
                            />
                            {t("hoa.payments.reminderModal.selectAll")}
                        </label>
                        <span className="text-[12px] tabular-nums text-gray-500">
                            {t("hoa.payments.reminderModal.chosen", {
                                count: chosen.size,
                                total: rows.length,
                            })}
                        </span>
                    </div>

                    <ul className="max-h-[22rem] space-y-1.5 overflow-y-auto">
                        {rows.map((r) => {
                            const on = chosen.has(r.invoice_id);
                            return (
                                <li key={r.invoice_id}>
                                    <label
                                        className={`flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 ring-1 transition ${
                                            on
                                                ? "bg-white ring-[#C3CDEC]"
                                                : "bg-gray-50/60 ring-transparent hover:bg-gray-50"
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={on}
                                            onChange={() => toggle(r.invoice_id)}
                                            className="h-4 w-4 shrink-0 rounded border-gray-300 text-[#1F3A93] focus:ring-[#1F3A93]"
                                        />
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-[14px] font-semibold text-gray-900">
                                                {r.resident}
                                            </span>
                                            <span className="mt-0.5 block truncate text-[11px] text-gray-500">
                                                {r.unit ? `${r.unit} · ` : ""}
                                                {fmtPeriod(r.period)} · {r.email}
                                            </span>
                                        </span>
                                        <span className="shrink-0 text-right">
                                            <Money usd={r.balance_due} />
                                            <span className="mt-0.5 block">
                                                <OverdueChip days={r.days_overdue} />
                                            </span>
                                        </span>
                                    </label>
                                </li>
                            );
                        })}
                    </ul>

                    <p className="text-[11px] leading-relaxed text-gray-500">
                        {t("hoa.payments.reminderModal.footnote")}
                    </p>
                </div>
            )}
        </ResponsiveModal>
    );
}

/**
 * A secondary toolbar action.
 *
 * Labels collapse below `sm` so three actions plus a primary still fit a
 * 360px viewport as one row instead of wrapping into a second.
 */
function ToolbarButton({
    icon: Icon,
    label,
    onClick,
    title,
    badge,
}: {
    icon: React.ElementType;
    label: string;
    onClick: () => void;
    title?: string;
    badge?: number;
}) {
    return (
        <button
            onClick={onClick}
            title={title ?? label}
            aria-label={label}
            className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-xl bg-white px-3 text-sm font-medium text-gray-700 ring-1 ring-gray-200 transition hover:bg-gray-50 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93]"
        >
            <Icon className="h-4 w-4" aria-hidden />
            <span className="hidden md:inline">{label}</span>
            {badge !== undefined && (
                <span
                    className="rounded-full px-1.5 py-px text-[10px] font-bold tabular-nums"
                    style={{ background: OVERDUE_TONE.bg, color: OVERDUE_TONE.fg }}
                >
                    {badge}
                </span>
            )}
        </button>
    );
}

/* ================================================================== *
 * Notification delivery log
 *
 * Was a bare eight-column table in a horizontally scrolling box — the only
 * list on this page with no mobile view at all, while the invoices beside it
 * had cards. On a phone you swiped sideways through columns.
 *
 * The "Invoice" column showed `id.slice(0, 8)`: eight hex characters of a
 * UUID. So the one question a delivery log exists to answer -- whose
 * reminder failed -- could not be answered without pasting an id somewhere
 * else. The serializer now sends the resident, unit and period.
 *
 * Grouped by day, because a log is read newest-first and "when" is the axis
 * people scan; and failures carry their error inline and a retry, rather
 * than a permanently-present Error column that is "—" on 95% of rows.
 * ================================================================== */
type NotifTypeFilter = "ALL" | NotificationType;
type NotifStatusFilter = "ALL" | NotificationStatus;

const NOTIF_STATUS_TONE: Record<NotificationStatus, { bg: string; fg: string; ring: string }> = {
    SENT: { bg: "#DCF5E6", fg: "#12693C", ring: "#B4E4C7" },
    FAILED: { bg: "#FEE2E2", fg: "#991B1B", ring: "#F8B4B4" },
    // Suppressed rather than broken: an SMS the provider was never asked to
    // send. It must not read as a failure.
    SKIPPED: { bg: "#F1F5F9", fg: "#475569", ring: "#E2E8F0" },
    MOCKED: { bg: "#F1F5F9", fg: "#475569", ring: "#E2E8F0" },
};

function NotificationLog({
    rows,
    isLoading,
    isFetching,
    onRefresh,
    typeFilter,
    setTypeFilter,
    statusFilter,
    setStatusFilter,
}: {
    rows: InvoiceNotification[];
    isLoading: boolean;
    isFetching: boolean;
    onRefresh: () => void;
    typeFilter: NotifTypeFilter;
    setTypeFilter: (v: NotifTypeFilter) => void;
    statusFilter: NotifStatusFilter;
    setStatusFilter: (v: NotifStatusFilter) => void;
}) {
    const { t } = useLanguage();

    const filtered = React.useMemo(
        () =>
            rows.filter(
                (n) =>
                    (typeFilter === "ALL" || n.notification_type === typeFilter) &&
                    (statusFilter === "ALL" || n.status === statusFilter)
            ),
        [rows, typeFilter, statusFilter]
    );

    /** Counts describe the WHOLE log, not the filtered view, so the failure
     *  figure does not vanish the moment you filter to SENT. */
    const totals = React.useMemo(() => {
        const by = { SENT: 0, FAILED: 0, MOCKED: 0, SKIPPED: 0 } as Record<NotificationStatus, number>;
        for (const n of rows) by[n.status] = (by[n.status] ?? 0) + 1;
        return by;
    }, [rows]);

    /** Newest first, grouped by calendar day. */
    const days = React.useMemo(() => {
        const map = new Map<string, InvoiceNotification[]>();
        for (const n of [...filtered].sort(
            (a, b) => +new Date(b.sent_at) - +new Date(a.sent_at)
        )) {
            const key = n.sent_at.slice(0, 10);
            map.set(key, [...(map.get(key) ?? []), n]);
        }
        return [...map.entries()];
    }, [filtered]);

    return (
        <div className="space-y-4">
            {/* Health first: how many failed is the reason to open this tab. */}
            <div className="-mx-4 flex snap-x gap-2.5 overflow-x-auto px-4 pb-1 sm:mx-0 sm:grid sm:grid-cols-3 sm:overflow-visible sm:px-0">
                {(["SENT", "FAILED", "MOCKED", "SKIPPED"] as NotificationStatus[]).map((k) => (
                    <KpiTile
                        key={k}
                        label={t(`hoa.notifLog.status.${k.toLowerCase()}`)}
                        value={String(totals[k] ?? 0)}
                        tone={tileStyle(NOTIF_STATUS_TONE[k] as never)}
                        active={statusFilter === k}
                        onClick={() => setStatusFilter(statusFilter === k ? "ALL" : k)}
                    />
                ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value as NotifTypeFilter)}
                    className={`h-10 rounded-xl bg-white px-3 text-sm ring-1 transition focus:outline-none focus:ring-2 focus:ring-[#1F3A93] ${
                        typeFilter === "ALL"
                            ? "text-gray-700 ring-gray-200"
                            : "bg-[#E7EBF8] font-medium text-[#1B3480] ring-[#C3CDEC]"
                    }`}
                >
                    <option value="ALL">{t("hoa.notifLog.allTypes")}</option>
                    <option value="INVOICE_SENT">{t("hoa.notifLog.typeInvoiceSent")}</option>
                    <option value="OVERDUE_REMINDER">{t("hoa.notifLog.typeOverdue")}</option>
                </select>

                <button
                    onClick={onRefresh}
                    disabled={isFetching}
                    aria-label={t("hoa.notifLog.refresh")}
                    title={t("hoa.notifLog.refresh")}
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-gray-600 ring-1 ring-gray-200 transition hover:bg-gray-50 disabled:opacity-60"
                >
                    <RotateCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                </button>

                {(typeFilter !== "ALL" || statusFilter !== "ALL") && (
                    <button
                        onClick={() => {
                            setTypeFilter("ALL");
                            setStatusFilter("ALL");
                        }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 underline-offset-2 hover:text-gray-800 hover:underline"
                    >
                        <X className="h-3 w-3" />
                        {t("common.reset")}
                    </button>
                )}

                <span className="ml-auto text-xs tabular-nums text-gray-400">
                    {t("hoa.notifLog.records", { count: String(filtered.length) })}
                </span>
            </div>

            {isLoading ? (
                <PaymentListSkeleton rows={5} />
            ) : days.length === 0 ? (
                <PaymentEmptyState
                    title={t("hoa.notifLog.noResults")}
                    hint={
                        typeFilter !== "ALL" || statusFilter !== "ALL"
                            ? t("hoa.payments.noResultsFiltered")
                            : undefined
                    }
                />
            ) : (
                <div className="space-y-5">
                    {days.map(([day, entries]) => (
                        <section key={day}>
                            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
                                {fmtDate(day)}
                                <span className="ml-2 font-medium normal-case tracking-normal text-gray-400">
                                    {t("hoa.notifLog.records", { count: String(entries.length) })}
                                </span>
                            </h3>
                            <ul className="space-y-2">
                                {entries.map((n) => (
                                    <NotificationRow key={n.id} n={n} />
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>
            )}
        </div>
    );
}

function NotificationRow({ n }: { n: InvoiceNotification }) {
    const { t } = useLanguage();
    const queryClient = useQueryClient();
    const tone = NOTIF_STATUS_TONE[n.status] ?? NOTIF_STATUS_TONE.MOCKED;
    const failed = n.status === "FAILED";

    const resend = useMutation({
        mutationFn: () => resendNotification(n.id),
        onSuccess: () => {
            toast.success(t("hoa.notifLog.resendOk"));
            queryClient.invalidateQueries({ queryKey: ["hoa-admin", "notification-log"] });
        },
        onError: () => toast.error(t("hoa.notifLog.resendFailed")),
    });

    // Falls back to the truncated id only when the join produced nothing --
    // an invoice deleted out from under its log, which should be rare and
    // should look different from a normal row rather than being the norm.
    const who = n.resident_name || (n.invoice ? `#${n.invoice.slice(0, 8)}` : "—");

    return (
        <li
            className="rounded-2xl border-l-4 bg-white/80 px-3.5 py-3 ring-1 ring-black/5 transition hover:bg-white"
            style={{ borderLeftColor: tone.ring }}
        >
            <div className="flex items-start gap-3">
                <span
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-xl"
                    style={{ background: tone.bg, color: tone.fg }}
                    aria-hidden
                >
                    {n.channel === "EMAIL" ? (
                        <Mail className="h-4 w-4" />
                    ) : (
                        <Smartphone className="h-4 w-4" />
                    )}
                </span>

                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                        <p className="min-w-0 truncate text-[14px] font-semibold text-gray-900">
                            {who}
                        </p>
                        <span className="shrink-0 text-[11px] tabular-nums text-gray-400">
                            {new Date(n.sent_at).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                            })}
                        </span>
                    </div>

                    <p className="mt-0.5 truncate text-[11px] text-gray-500">
                        {[n.unit_code, n.invoice_period && fmtPeriod(n.invoice_period)]
                            .filter(Boolean)
                            .join(" · ")}
                        {n.unit_code || n.invoice_period ? " · " : ""}
                        {n.recipient_email || n.recipient_phone || "—"}
                    </p>

                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <span
                            className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
                            style={{ background: tone.bg, color: tone.fg }}
                        >
                            {t(`hoa.notifLog.status.${n.status.toLowerCase()}`)}
                        </span>
                        <QuietChip>
                            {n.notification_type === "OVERDUE_REMINDER"
                                ? t("hoa.notifLog.typeOverdue")
                                : t("hoa.notifLog.typeInvoiceSent")}
                        </QuietChip>
                        <QuietChip>
                            {n.triggered_by_name || t("hoa.notifLog.triggeredAuto")}
                        </QuietChip>
                    </div>

                    {/* The error only exists on failures, so it lives on the
                        failures rather than in a column that is "—" on almost
                        every row. */}
                    {failed && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            {n.error_message && (
                                <p className="min-w-0 flex-1 break-words rounded-lg bg-red-50 px-2.5 py-1.5 text-[11px] text-red-800 ring-1 ring-red-100">
                                    {n.error_message}
                                </p>
                            )}
                            <button
                                type="button"
                                onClick={() => resend.mutate()}
                                disabled={resend.isPending}
                                className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg bg-white px-2.5 text-[12px] font-semibold text-gray-700 ring-1 ring-gray-200 transition hover:bg-gray-50 disabled:opacity-50"
                            >
                                {resend.isPending ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                                ) : (
                                    <RotateCw className="h-3.5 w-3.5" aria-hidden />
                                )}
                                {t("hoa.notifLog.resend")}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </li>
    );
}
